const { useState, useEffect, useMemo, useContext, useCallback, useRef } = React;
import StickyPageTitle from "../../../Components/Header/Sticky";
import { defaultCompareWindowForPrimary } from "../../../Components/Filter/filterDatePresets.js";
import { DomainContext } from "../../../App.js";
import API from "../../../API/api";
import { ScannerHost } from "../../../API/host";
import Authentication from "../../../Authentication/Auth";
import "../../Dashboard/Style.css";
import "./MarketingReport.css";
import {
    useSyncDomainFromRoute,
    consentsDomainFromRoute,
    toDomainsApiHeader,
    reportsPath,
} from "../../../Functions/domainPathSegments.js";
import {
    MarketingOverviewCharts,
    MarketingChannelCharts,
    MarketingTimeseriesChart,
} from "./MarketingCharts.js";
import MarketingSuggestionsStrip from "./MarketingSuggestionsStrip.js";
import { buildInvisibleTrafficSuggestions } from "./marketingSuggestions.js";
import appStorage from '../../../Functions/storage.js';

const useParams = window.ReactRouterDOM.useParams;

function toYmd(d) {
    if (!d) return "";
    try {
        if (typeof d === "string") return d.split("T")[0];
        return d.toISOString().split("T")[0];
    } catch {
        return "";
    }
}

/** Accept several possible API shapes */
function extractRows(payload) {
    if (payload == null) return [];
    const root = payload.data != null ? payload.data : payload;
    if (Array.isArray(root)) return root;
    if (Array.isArray(root.rows)) return root.rows;
    if (Array.isArray(root.campaigns)) return root.campaigns;
    if (Array.isArray(root.items)) return root.items;
    if (Array.isArray(root.attribution)) return root.attribution;
    return [];
}

function extractSummary(payload) {
    if (payload == null) return null;
    const root = payload.data != null ? payload.data : payload;
    if (root && typeof root === "object" && root.summary && typeof root.summary === "object") return root.summary;
    if (root && typeof root === "object" && root.totals && typeof root.totals === "object") return root.totals;
    return null;
}

// ── Ad-platform UTM matching ─────────────────────────────────────────────────
const AD_PLATFORM_LABELS = {
    google_ads:    "Google Ads",
    meta_ads:      "Meta Ads",
    linkedin_ads:  "LinkedIn Ads",
    microsoft_ads: "Microsoft Ads",
};
const AD_PLATFORM_PATTERNS = {
    google_ads:    /^(?:google|adwords|gads)/,
    meta_ads:      /^(?:facebook|meta|instagram|messenger|fb|ig|msg|an$)/,
    linkedin_ads:  /^(?:linkedin|liads)/,
    microsoft_ads: /^(?:bing|microsoft|msads|msn)/,
};
function canonAdSource(s) {
    return String(s || "").toLowerCase().replace(/[\s_\-.]+/g, "");
}
function consentsForAdPlatform(channelOverview, platformId) {
    const pattern = AD_PLATFORM_PATTERNS[platformId];
    if (!pattern) return 0;
    return channelOverview
        .filter(row => pattern.test(canonAdSource(row.channel)))
        .reduce((sum, row) => sum + (row.consents || 0), 0);
}
function formatAdMoney(n, currency) {
    const SYMBOLS = { EUR: "€", USD: "$", GBP: "£", CHF: "CHF", DKK: "kr", SEK: "kr", NOK: "kr" };
    const sym = SYMBOLS[currency] || (currency ? currency + " " : "");
    return `${sym}${Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Prefer API summary (full dataset) when rows are paginated. */
function pickTotalConsentsFromSummary(summary, rowList) {
    const v = summary && summary.totalConsents != null ? Number(summary.totalConsents) : NaN;
    if (Number.isFinite(v) && v >= 0) return v;
    return rowList.reduce((s, r) => s + (Number(r.consents) || 0), 0);
}

function pickMeasurementReadyCount(summary, rowList) {
    const v = summary && summary.measurementReadyConsents != null ? Number(summary.measurementReadyConsents) : NaN;
    if (Number.isFinite(v) && v >= 0) return v;
    return rowList.reduce((s, r) => s + (Number(r.acceptAll) || 0), 0);
}

function pickMeasurementReadySharePct(summary, rowList) {
    const v = summary && summary.measurementReadySharePct != null ? Number(summary.measurementReadySharePct) : NaN;
    if (Number.isFinite(v)) return v;
    const t = rowList.reduce((s, r) => s + (Number(r.consents) || 0), 0);
    const a = rowList.reduce((s, r) => s + (Number(r.acceptAll) || 0), 0);
    return t > 0 ? Math.round((a / t) * 1000) / 10 : null;
}

function compareRangeActive(compareRange) {
    return compareRange !== 0 && compareRange != null;
}

/*
 * Blind-spot row threshold.
 *
 * A row counts as an "analytics blind spot" when both:
 *   - it has enough volume to be worth worrying about (≥ 10 consents), and
 *   - fewer than half of those consents were accept-all (the measurable
 *     slice that actually reaches GA4 / Ads Manager / Meta pixel).
 *
 * The 10-consent floor keeps tiny, statistically-noisy rows from getting
 * the warning treatment. The 50% visibility cutoff is the industry-typical
 * "half your traffic is invisible" line — anything worse than that is
 * worth surfacing in the UI.
 */
const BLIND_SPOT_MIN_CONSENTS = 10;
const BLIND_SPOT_MAX_VISIBILITY_PCT = 50;

function isAnalyticsBlindSpot(row) {
    const consents = Number(row?.consents) || 0;
    if (consents < BLIND_SPOT_MIN_CONSENTS) return false;
    const visibility = Number(row?.acceptPct);
    if (!Number.isFinite(visibility)) return false;
    return visibility < BLIND_SPOT_MAX_VISIBILITY_PCT;
}

/** Stable join key for matching primary-period rows to baseline-period rows. */
function marketingAttributionRowKey(utmSource, utmMedium, rawCampaign, referrer) {
    return [
        normUtm(utmSource),
        normUtm(utmMedium),
        normUtm(String(rawCampaign ?? "").trim()),
        normUtm(referrer),
    ].join("|");
}

function aggregateBaselineByRowKey(baselineRows) {
    const m = new Map();
    for (const r of baselineRows) {
        const k = r.rowKey;
        if (!k) continue;
        if (!m.has(k)) {
            m.set(k, {
                consents: 0,
                acceptAll: 0,
                essentialOnly: 0,
                granular: 0,
                acceptNum: 0,
                acceptDen: 0,
            });
        }
        const x = m.get(k);
        x.consents += Number(r.consents) || 0;
        x.acceptAll += Number(r.acceptAll) || 0;
        x.essentialOnly += Number(r.essentialOnly) || 0;
        x.granular += Number(r.granular) || 0;
        if (r.acceptPct != null && Number.isFinite(r.acceptPct) && r.consents > 0) {
            x.acceptNum += r.acceptPct * r.consents;
            x.acceptDen += r.consents;
        }
    }
    const out = new Map();
    for (const [k, x] of m) {
        const acceptPct =
            x.acceptDen > 0 ? Math.round((x.acceptNum / x.acceptDen) * 10) / 10 : null;
        out.set(k, {
            consents: x.consents,
            acceptAll: x.acceptAll,
            essentialOnly: x.essentialOnly,
            granular: x.granular,
            acceptPct,
        });
    }
    return out;
}

/**
 * Attach baseline metrics to each primary row (matched by rowKey).
 * When no baseline row exists, prev* fields are null (treat as new / unmatched in baseline).
 */
function attachBaselineToRows(primaryRows, baselineRows) {
    if (!baselineRows?.length) {
        return primaryRows.map((r) => ({
            ...r,
            prevConsents: null,
            prevAcceptPct: null,
            prevAcceptAll: null,
            prevEssentialOnly: null,
            prevGranular: null,
        }));
    }
    const agg = aggregateBaselineByRowKey(baselineRows);
    return primaryRows.map((r) => {
        const b = agg.get(r.rowKey);
        if (!b) {
            return {
                ...r,
                prevConsents: null,
                prevAcceptPct: null,
                prevAcceptAll: null,
                prevEssentialOnly: null,
                prevGranular: null,
            };
        }
        return {
            ...r,
            prevConsents: b.consents,
            prevAcceptPct: b.acceptPct,
            prevAcceptAll: b.acceptAll,
            prevEssentialOnly: b.essentialOnly,
            prevGranular: b.granular,
        };
    });
}

/** Merge channel-level baseline totals for the overview table (all campaigns in baseline window). */
function mergeChannelOverviewWithBaseline(currentOverview, baselineRows) {
    if (!baselineRows?.length) {
        return currentOverview.map((c) => ({
            ...c,
            prevConsents: null,
            prevCampaignCount: null,
            prevAcceptPct: null,
            prevAcceptAll: null,
            prevEssentialOnly: null,
            prevGranular: null,
        }));
    }
    const baselineOverview = buildChannelOverview(baselineRows);
    const bMap = new Map(baselineOverview.map((x) => [x.channel, x]));
    return currentOverview.map((c) => {
        const b = bMap.get(c.channel);
        if (!b) {
            return {
                ...c,
                prevConsents: null,
                prevCampaignCount: null,
                prevAcceptPct: null,
                prevAcceptAll: null,
                prevEssentialOnly: null,
                prevGranular: null,
            };
        }
        return {
            ...c,
            prevConsents: b.consents,
            prevCampaignCount: b.campaignCount,
            prevAcceptPct: b.acceptPct,
            prevAcceptAll: b.acceptAll,
            prevEssentialOnly: b.essentialOnly,
            prevGranular: b.granular,
        };
    });
}

function formatPctChange(current, previous) {
    if (current == null) return null;
    const c = Number(current);
    const p = Number(previous);
    if (!Number.isFinite(c)) return null;
    if (previous == null || !Number.isFinite(p)) return null;
    if (p === 0 && c === 0) return "0%";
    if (p === 0) return "—";
    const pct = ((c - p) / p) * 100;
    const rounded = Math.round(pct * 10) / 10;
    const sign = rounded > 0 ? "+" : "";
    return `${sign}${rounded.toLocaleString("de-DE", { maximumFractionDigits: 1 })}%`;
}

function formatPtsChange(current, previous) {
    if (current == null) return null;
    const c = Number(current);
    const p = Number(previous);
    if (!Number.isFinite(c) || previous == null || !Number.isFinite(p)) return null;
    const d = c - p;
    const rounded = Math.round(d * 10) / 10;
    const sign = rounded > 0 ? "+" : "";
    return `${sign}${rounded.toLocaleString("de-DE", { maximumFractionDigits: 1 })} pts`;
}

function marketingCompareVolumeClass(current, previous) {
    if (current == null || previous == null) return "";
    const c = Number(current);
    const p = Number(previous);
    if (!Number.isFinite(p) || !Number.isFinite(c)) return "";
    if (p === 0) return c > 0 ? "marketing-report-delta--up" : "";
    const pct = ((c - p) / p) * 100;
    if (pct > 0.5) return "marketing-report-delta--up";
    if (pct < -0.5) return "marketing-report-delta--down";
    return "marketing-report-delta--flat";
}

function normUtm(s) {
    return String(s ?? "")
        .trim()
        .toLowerCase();
}

/**
 * Shorten redundant Capterra UTM fragments in the campaign cell, e.g. utm_Capterra=capterra → Capterra.
 */
function simplifyCampaignDisplay(campaignRaw) {
    let t = String(campaignRaw ?? "").trim();
    if (!t || t === "—") {
        return t || "—";
    }
    t = t.replace(/\butm_capterra\s*=\s*capterra\b/gi, "Capterra");
    t = t.replace(/\s*·\s*(?:·\s*)+/g, " · ").replace(/^\s*·\s*|\s*·\s*$/g, "").trim();
    t = t.replace(/\s{2,}/g, " ");
    return t === "" ? "—" : t;
}

/*
 * Known search-engine hosts for classifying untagged organic traffic.
 *
 * We match Google's country TLDs (google.com, google.de, google.co.uk,
 * google.com.au…) with a regex, and the rest of the long tail with an
 * exact-host set. Hostnames are compared lowercased and with a stripped
 * `www.` prefix. The list intentionally excludes platform-specific
 * hosts (instagram.com, facebook.com, linkedin.com, etc.) because those
 * are already classified as platform-organic channels earlier in
 * `deriveMarketingChannel` and we don't want to collapse that detail.
 */
const SEARCH_ENGINE_HOSTS = new Set([
    "bing.com",
    "duckduckgo.com",
    "yahoo.com",
    "search.yahoo.com",
    "baidu.com",
    "yandex.com",
    "yandex.ru",
    "ecosia.org",
    "qwant.com",
    "startpage.com",
    "search.brave.com",
    "ask.com",
    "naver.com",
    "kagi.com",
    "search.aol.com",
    "search.aol.co.uk",
    "presearch.com",
]);

function isSearchEngineHost(host) {
    if (!host || host === "—") return false;
    const h = String(host).toLowerCase().replace(/^www\./, "");
    if (!h) return false;
    if (/^google(?:\.[a-z]{2,}){1,3}$/.test(h)) return true;
    return SEARCH_ENGINE_HOSTS.has(h);
}

/**
 * Google paid traffic → single "Google Ads" bucket (Search, Display, legacy AdWords / PPC labels).
 */
function isGoogleOrganicMedium(mediumNorm) {
    const m = mediumNorm;
    return m === "organic" || m.includes("organic") || m === "natural" || m === "seo";
}

function isGooglePaidMedium(mediumNorm) {
    const m = mediumNorm;
    return (
        m.includes("cpc") ||
        m.includes("ppc") ||
        m === "paid" ||
        m.includes("paidsearch") ||
        m.includes("paid search") ||
        m.includes("adwords") ||
        m.includes("display") ||
        m.includes("cpm") ||
        /cpc|ppc|paid|social|ads|display|paidsocial|paid_social/.test(m)
    );
}

function isGoogleAdsFamilySource(sourceNorm) {
    const s = sourceNorm;
    return (
        s.includes("google") ||
        s === "google ads" ||
        s.includes("googleads") ||
        s.includes("google_ads") ||
        s.includes("adwords")
    );
}

/**
 * Meta Ads Manager often sends placement as utm_medium (e.g. Facebook_Mobile_Feed, Instagram_Stories)
 * without "cpc" / "paid" — still paid inventory.
 */
function isMetaPaidPlacementMedium(mediumNorm) {
    const m = mediumNorm;
    if (!m || m === "—") return false;
    if (/mobile_feed|desktop_feed|inline_feed|instagram_stories|instagram_story|instagram_reels|instagram_reel/i.test(m)) {
        return true;
    }
    if (/(^|_)(facebook|fb|meta|instagram|ig)($|_)/i.test(m) && /(_feed|_stories|_story|_reels|_reel|marketplace|messenger|carousel|_search|instream|video)/i.test(m)) {
        return true;
    }
    if ((m.includes("facebook") || m.includes("instagram")) && (m.includes("feed") || m.includes("story") || m.includes("reel"))) {
        return true;
    }
    return false;
}

/**
 * Human-readable channel for the marketing table (source / medium / referrer heuristics).
 */
function deriveMarketingChannel(row) {
    const s = normUtm(row.utmSource);
    const m = normUtm(row.utmMedium);
    const campaign = normUtm(row.utmCampaign);
    const host = normUtm(row.referrer).replace(/^www\./, "");
    const paidLike = /cpc|ppc|paid|social|ads|display|paidsocial|paid_social/.test(m);
    const metaPaidPlacement = isMetaPaidPlacementMedium(m);
    /*
     * A row is "untagged" when neither utm_source nor utm_medium carry
     * a real value (both null/empty or the COALESCE'd "—" placeholder).
     * Platform-family host checks below still short-circuit first, so
     * organic Instagram / Facebook / LinkedIn / X referrals keep their
     * own labels. Everything that falls past those checks gets routed
     * into Direct / Organic search / Referral by referrer host.
     */
    const isUntagged = (!s || s === "—") && (!m || m === "—");

    if (s === "(fbclid)") return "Facebook Ads";
    if (s === "(gclid)") return "Google Ads";
    if (s === "(utm)") return "Marketing (custom parameters)";
    if (campaign.includes("capterra") || s.includes("capterra")) return "Capterra";

    if (s.includes("instagram") || host.includes("instagram.com")) {
        return paidLike || metaPaidPlacement ? "Instagram Ads" : "Instagram";
    }

    const isFacebookFamily =
        s === "fb" ||
        s === "facebook" ||
        s === "meta" ||
        s.includes("facebook") ||
        host.includes("facebook.com") ||
        host.includes("fb.com") ||
        host.includes("m.facebook.com");

    if (isFacebookFamily) {
        const isPaidFacebook = paidLike || metaPaidPlacement;
        return isPaidFacebook ? "Facebook Ads" : "Facebook (Organic)";
    }

    const fromGoogleReferrer = host.includes("google.") && !host.includes("doubleclick.net");
    const googleSource = isGoogleAdsFamilySource(s);
    if (googleSource || fromGoogleReferrer) {
        /*
         * A referrer from google.com with no UTM tags at all is
         * an organic SERP click, not a campaign. Routing it under
         * "Google" used to conflate it with branded paid traffic
         * and inflated the "Google" channel with SEO visits.
         */
        if (isUntagged && fromGoogleReferrer) return "Organic search";
        if (isGoogleOrganicMedium(m)) return "Organic search";
        if (isGooglePaidMedium(m) || paidLike) return "Google Ads";
        return "Google";
    }

    // Legacy UTM: "ppc" as source only (old labels) — fold into Google Ads when clearly paid
    if (!s.includes("bing") && !s.includes("microsoft")) {
        const legacyPpcSource =
            s === "ppc" || s.startsWith("ppc/") || (s.includes("ppc") && s.includes("adwords"));
        if (legacyPpcSource && (isGooglePaidMedium(m) || paidLike)) {
            return "Google Ads";
        }
    }

    if (s.includes("linkedin") || host.includes("linkedin.com")) {
        return paidLike ? "LinkedIn Ads" : "LinkedIn";
    }
    if (
        s.includes("twitter") ||
        s === "tw" ||
        host.includes("twitter.com") ||
        host.includes("x.com") ||
        host.includes("t.co")
    ) {
        return paidLike ? "X (Twitter) Ads" : "X (Twitter)";
    }
    if (s.includes("bing") || s.includes("microsoft")) return "Microsoft Ads";
    if (s.includes("tiktok")) return paidLike ? "TikTok Ads" : "TikTok";
    if (s.includes("pinterest")) return paidLike ? "Pinterest Ads" : "Pinterest";
    if (m === "email" || m.includes("newsletter") || m.includes("e-mail")) return "Email";
    if (m.includes("affiliate")) return "Affiliate";
    if (m === "organic" || s === "organic") return "Organic search";

    const hasDims = (s && s !== "—") || (m && m !== "—");
    if (hasDims) {
        const srcLabel = row.utmSource === "—" ? "" : String(row.utmSource).trim();
        const medLabel = row.utmMedium === "—" ? "" : String(row.utmMedium).trim();
        if (srcLabel && medLabel) return `${medLabel} · ${srcLabel}`;
        if (srcLabel) return srcLabel;
        if (medLabel) return medLabel;
    }

    /*
     * Untagged traffic fallback. Three buckets:
     *   - no referrer host        → Direct traffic (typed URL, bookmark, app, etc.)
     *   - referrer is a search UI → Organic search (Bing, DuckDuckGo, Yahoo, Yandex, Ecosia, …)
     *   - any other referrer      → Referral (host) — a blog post, partner link, email client, etc.
     * Google organic is handled earlier in the Google block.
     */
    if (host && host !== "—") {
        if (isSearchEngineHost(host)) return "Organic search";
        const pretty = String(row.referrer).replace(/^www\./i, "");
        return `Referral (${pretty})`;
    }

    return "Direct traffic";
}

function mapRow(r) {
    const ref =
        r.referrerHost ??
        r.referrer_host ??
        r.referrer ??
        r.referrerUrl ??
        r.referrer_url ??
        (r.referrerDomain != null ? String(r.referrerDomain) : "—");
    const rawCampaign = String(r.utm_campaign ?? r.utmCampaign ?? r.campaign ?? "—");
    const utmSource = String(r.utm_source ?? r.utmSource ?? r.source ?? "—");
    const utmMedium = String(r.utm_medium ?? r.utmMedium ?? r.medium ?? "—");
    const referrerNorm = ref === "" || ref == null ? "—" : String(ref);
    const base = {
        referrer: referrerNorm,
        utmSource,
        utmMedium,
        utmCampaign: simplifyCampaignDisplay(rawCampaign),
        consents: Number(r.consents ?? r.consent_count ?? r.count ?? r.total ?? 0) || 0,
        acceptPct:
            r.acceptRate != null
                ? Number(r.acceptRate)
                : r.accepted_pct != null
                  ? Number(r.accepted_pct)
                  : r.accept_pct != null
                    ? Number(r.accept_pct)
                    : null,
        acceptAll: Number(r.acceptAll ?? r.accept_all ?? 0) || 0,
        essentialOnly: Number(r.essentialOnly ?? r.essential_only ?? 0) || 0,
        granular:
            Number(
                r.granular ??
                    r.granular_choices ??
                    r.granularChoices ??
                    r.granular_consents ??
                    r.granularConsents ??
                    r.partial ??
                    r.partial_consents ??
                    r.partialConsents ??
                    r.custom ??
                    r.custom_choice ??
                    r.customChoice ??
                    r.custom_consents ??
                    r.customConsents ??
                    0
            ) || 0,
        context: normalizeContext(r.context),
        rowKey: marketingAttributionRowKey(utmSource, utmMedium, rawCampaign, referrerNorm),
    };
    return {
        ...base,
        channel: deriveMarketingChannel({ ...base, utmCampaign: rawCampaign }),
    };
}

function normalizeContext(ctx) {
    if (!ctx || typeof ctx !== "object") {
        return null;
    }
    return {
        topCountries: Array.isArray(ctx.topCountries) ? ctx.topCountries : [],
        topLandingPaths: Array.isArray(ctx.topLandingPaths) ? ctx.topLandingPaths : [],
        topUtmContent: Array.isArray(ctx.topUtmContent) ? ctx.topUtmContent : [],
        topUtmTerms: Array.isArray(ctx.topUtmTerms) ? ctx.topUtmTerms : [],
    };
}

function mergeContextDim(rows, listKey, idFn, topN) {
    const map = new Map();
    for (const r of rows) {
        const list = r.context?.[listKey];
        if (!Array.isArray(list)) {
            continue;
        }
        for (const it of list) {
            const id = String(idFn(it) ?? "—");
            const cur = map.get(id) ?? { consents: 0, acceptAll: 0, essentialOnly: 0, granular: 0 };
            cur.consents += Number(it.consents) || 0;
            cur.acceptAll += Number(it.acceptAll) || 0;
            cur.essentialOnly += Number(it.essentialOnly) || 0;
            cur.granular += Number(it.granular) || 0;
            map.set(id, cur);
        }
    }
    return [...map.entries()]
        .map(([id, st]) => {
            const decided = st.acceptAll + st.essentialOnly + st.granular;
            const acceptPct =
                decided > 0 ? Math.round((st.acceptAll / decided) * 1000) / 10 : null;
            return { id, ...st, acceptPct };
        })
        .sort((a, b) => b.consents - a.consents)
        .slice(0, topN);
}

function mergeAllContext(rows) {
    const topCountries = mergeContextDim(rows, "topCountries", (it) => it.country, 12);
    const topLandingPaths = mergeContextDim(rows, "topLandingPaths", (it) => it.path, 8);
    const topUtmContent = mergeContextDim(rows, "topUtmContent", (it) => it.value, 5);
    const topUtmTerms = mergeContextDim(rows, "topUtmTerms", (it) => it.value, 5);
    const hasAny =
        topCountries.length > 0 ||
        topLandingPaths.length > 0 ||
        topUtmContent.length > 0 ||
        topUtmTerms.length > 0;
    return { topCountries, topLandingPaths, topUtmContent, topUtmTerms, hasAny };
}

function escapeCsvCell(value) {
    const s = value == null ? "" : String(value);
    if (/[",\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
}

function summarizeContextList(list, formatter) {
    if (!Array.isArray(list) || list.length === 0) {
        return "";
    }
    return list
        .map(formatter)
        .join(" | ")
        .replace(/\r?\n/g, " ");
}

function csvRelativeChangeNumber(current, previous) {
    const c = Number(current);
    const p = Number(previous);
    if (previous == null || !Number.isFinite(p) || !Number.isFinite(c)) return "";
    if (p === 0) return c === 0 ? "0" : "";
    return String(Math.round(((c - p) / p) * 10000) / 100);
}

function buildMarketingCsvCampaignRows(rows, meta) {
    const cmp = Boolean(meta.compareExport);
    const lines = [
        `# Marketing — campaign rows`,
        `# From: ${meta.from}; To: ${meta.to}; Scope: ${meta.scope}`,
        ...(meta.compareOn ? [`# Compare: ${meta.compareFrom} – ${meta.compareTo}`] : []),
        `# Generated: ${meta.generatedAt}`,
    ];
    const header = [
        "channel",
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "referrer_host",
        "consents",
        "accept_rate_pct",
        "accept_all",
        "essential_only",
        "granular",
        "top_countries",
        "top_landing_paths",
        "top_utm_content",
        "top_utm_terms",
    ];
    if (cmp) {
        header.push(
            "baseline_consents",
            "consents_change_pct",
            "baseline_accept_rate_pct",
            "accept_rate_pts_change"
        );
    }
    lines.push(header.join(","));
    for (const r of rows) {
        const ctx = r.context;
        const topCountries = summarizeContextList(ctx?.topCountries, (x) => `${x.country}:${x.consents}`);
        const topPaths = summarizeContextList(ctx?.topLandingPaths, (x) => `${x.path}:${x.consents}`);
        const topContent = summarizeContextList(ctx?.topUtmContent, (x) => `${x.value}:${x.consents}`);
        const topTerms = summarizeContextList(ctx?.topUtmTerms, (x) => `${x.value}:${x.consents}`);
        const row = [
            r.channel,
            r.utmSource,
            r.utmMedium,
            r.utmCampaign,
            r.referrer,
            String(r.consents),
            r.acceptPct != null && Number.isFinite(r.acceptPct) ? String(r.acceptPct) : "",
            String(r.acceptAll ?? 0),
            String(r.essentialOnly ?? 0),
            String(r.granular ?? 0),
            topCountries,
            topPaths,
            topContent,
            topTerms,
        ];
        if (cmp) {
            const pts =
                r.acceptPct != null &&
                Number.isFinite(r.acceptPct) &&
                r.prevAcceptPct != null &&
                Number.isFinite(r.prevAcceptPct)
                    ? String(Math.round((r.acceptPct - r.prevAcceptPct) * 10) / 10)
                    : "";
            row.push(
                r.prevConsents != null ? String(r.prevConsents) : "",
                csvRelativeChangeNumber(r.consents, r.prevConsents),
                r.prevAcceptPct != null && Number.isFinite(r.prevAcceptPct) ? String(r.prevAcceptPct) : "",
                pts
            );
        }
        lines.push(row.map(escapeCsvCell).join(","));
    }
    return lines.join("\r\n");
}

function buildMarketingCsvChannelRows(channelOverview, meta) {
    const cmp = Boolean(meta.compareExport);
    const lines = [
        `# Marketing — channel overview`,
        `# From: ${meta.from}; To: ${meta.to}; Scope: ${meta.scope}`,
        ...(meta.compareOn ? [`# Compare: ${meta.compareFrom} – ${meta.compareTo}`] : []),
        `# Generated: ${meta.generatedAt}`,
    ];
    const header = [
        "channel",
        "campaigns",
        "consents",
        "accept_rate_pct_weighted",
        "accept_all",
        "essential_only",
        "granular",
    ];
    if (cmp) {
        header.push(
            "baseline_campaigns",
            "baseline_consents",
            "campaigns_change_pct",
            "consents_change_pct",
            "baseline_accept_rate_pct_weighted",
            "accept_rate_pts_change"
        );
    }
    lines.push(header.join(","));
    for (const r of channelOverview) {
        const row = [
            r.channel,
            String(r.campaignCount),
            String(r.consents),
            r.acceptPct != null && Number.isFinite(r.acceptPct) ? String(r.acceptPct) : "",
            String(r.acceptAll ?? 0),
            String(r.essentialOnly ?? 0),
            String(r.granular ?? 0),
        ];
        if (cmp) {
            const pts =
                r.acceptPct != null &&
                Number.isFinite(r.acceptPct) &&
                r.prevAcceptPct != null &&
                Number.isFinite(r.prevAcceptPct)
                    ? String(Math.round((r.acceptPct - r.prevAcceptPct) * 10) / 10)
                    : "";
            row.push(
                r.prevCampaignCount != null ? String(r.prevCampaignCount) : "",
                r.prevConsents != null ? String(r.prevConsents) : "",
                csvRelativeChangeNumber(r.campaignCount, r.prevCampaignCount),
                csvRelativeChangeNumber(r.consents, r.prevConsents),
                r.prevAcceptPct != null && Number.isFinite(r.prevAcceptPct) ? String(r.prevAcceptPct) : "",
                pts
            );
        }
        lines.push(row.map(escapeCsvCell).join(","));
    }
    return lines.join("\r\n");
}

function triggerCsvDownload(filename, csvText) {
    const blob = new Blob(["\uFEFF", csvText], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function MarketingContextSection({ heading, rows }) {
    const merged = useMemo(() => mergeAllContext(rows), [rows]);
    if (!merged.hasAny) {
        return (
            <section className="marketing-context marketing-context--empty" aria-labelledby="marketing-context-h">
                <h3 id="marketing-context-h" className="marketing-context__title">
                    {heading}
                </h3>
                <p className="marketing-context__empty-note">
                    No geographic or landing-path breakdown in the API response yet. After you deploy the updated{" "}
                    <code>marketingAttribution</code> endpoint, top countries, paths, and UTM content/term slices appear
                    here.
                </p>
            </section>
        );
    }

    const fmtPct = (p) =>
        p != null && Number.isFinite(p) ? `${p.toLocaleString("de-DE", { maximumFractionDigits: 1 })}%` : "—";

    const miniRows = (items) =>
        items.map((row) => (
            <tr key={row.id}>
                <td className="marketing-context__label">{row.id}</td>
                <td className="marketing-context__num">{row.consents.toLocaleString("de-DE")}</td>
                <td className="marketing-context__num">{fmtPct(row.acceptPct)}</td>
                <td className="marketing-context__split">
                    {formatChoiceCountPct(row.acceptAll, row.consents)} ·{" "}
                    {formatChoiceCountPct(row.essentialOnly, row.consents)} ·{" "}
                    {formatChoiceCountPct(row.granular, row.consents)}
                </td>
            </tr>
        ));

    return (
        <section className="marketing-context" aria-labelledby="marketing-context-h">
            <h3 id="marketing-context-h" className="marketing-context__title">
                {heading}
            </h3>
            <p className="marketing-context__lede">
                Merged from campaign-level slices in this view (union of each campaign’s top dimensions). Percentages
                use accept-all vs all classified choices within each slice.
            </p>
            <div className="marketing-context__grid">
                {merged.topCountries.length > 0 ? (
                    <div className="marketing-context__block">
                        <h3 className="marketing-context__block-title">Top countries</h3>
                        <table className="marketing-context__table">
                            <thead>
                                <tr>
                                    <th>Country</th>
                                    <th className="marketing-context__num">Consents</th>
                                    <th className="marketing-context__num">Accept %</th>
                                    <th>All / Essential / Granular</th>
                                </tr>
                            </thead>
                            <tbody>{miniRows(merged.topCountries)}</tbody>
                        </table>
                    </div>
                ) : null}
                {merged.topLandingPaths.length > 0 ? (
                    <div className="marketing-context__block">
                        <h3 className="marketing-context__block-title">Top landing paths</h3>
                        <table className="marketing-context__table">
                            <thead>
                                <tr>
                                    <th>Path</th>
                                    <th className="marketing-context__num">Consents</th>
                                    <th className="marketing-context__num">Accept %</th>
                                    <th>All / Essential / Granular</th>
                                </tr>
                            </thead>
                            <tbody>{miniRows(merged.topLandingPaths)}</tbody>
                        </table>
                    </div>
                ) : null}
                {merged.topUtmContent.length > 0 ? (
                    <div className="marketing-context__block">
                        <h3 className="marketing-context__block-title">UTM content</h3>
                        <table className="marketing-context__table">
                            <thead>
                                <tr>
                                    <th>Value</th>
                                    <th className="marketing-context__num">Consents</th>
                                    <th className="marketing-context__num">Accept %</th>
                                    <th>All / Essential / Granular</th>
                                </tr>
                            </thead>
                            <tbody>{miniRows(merged.topUtmContent)}</tbody>
                        </table>
                    </div>
                ) : null}
                {merged.topUtmTerms.length > 0 ? (
                    <div className="marketing-context__block">
                        <h3 className="marketing-context__block-title">UTM term</h3>
                        <table className="marketing-context__table">
                            <thead>
                                <tr>
                                    <th>Value</th>
                                    <th className="marketing-context__num">Consents</th>
                                    <th className="marketing-context__num">Accept %</th>
                                    <th>All / Essential / Granular</th>
                                </tr>
                            </thead>
                            <tbody>{miniRows(merged.topUtmTerms)}</tbody>
                        </table>
                    </div>
                ) : null}
            </div>
        </section>
    );
}

/** Count + share of consents in this row (same semantics as CMP payload classification). */
function formatChoiceCountPct(count, consents) {
    const c = Number(count) || 0;
    const t = Number(consents) || 0;
    if (t <= 0) return "—";
    const pct = (c / t) * 100;
    return `${c.toLocaleString("de-DE")} (${pct.toLocaleString("de-DE", { maximumFractionDigits: 1 })}%)`;
}

/** Weighted average acceptance % by consent volume (rows without accept % are skipped). */
function aggregateWeightedAcceptPct(rowList) {
    let numerator = 0;
    let denominator = 0;
    for (const r of rowList) {
        if (r.acceptPct == null || !Number.isFinite(r.acceptPct)) continue;
        numerator += r.acceptPct * r.consents;
        denominator += r.consents;
    }
    if (denominator <= 0) return null;
    return Math.round((numerator / denominator) * 10) / 10;
}

/** One row per channel for level-1 overview. */
function buildChannelOverview(rowList) {
    const byChannel = new Map();
    for (const r of rowList) {
        const ch = r.channel;
        if (!byChannel.has(ch)) byChannel.set(ch, []);
        byChannel.get(ch).push(r);
    }
    const out = [];
    for (const [channel, list] of byChannel) {
        const consents = list.reduce((s, x) => s + x.consents, 0);
        out.push({
            channel,
            consents,
            acceptPct: aggregateWeightedAcceptPct(list),
            campaignCount: list.length,
            acceptAll: list.reduce((s, x) => s + (x.acceptAll ?? 0), 0),
            essentialOnly: list.reduce((s, x) => s + (x.essentialOnly ?? 0), 0),
            granular: list.reduce((s, x) => s + (x.granular ?? 0), 0),
        });
    }
    out.sort((a, b) => b.consents - a.consents);
    return out;
}

function formatPeriodRange(fromDate, toDate) {
    const a = toYmd(fromDate);
    const b = toYmd(toDate);
    if (!a && !b) {
        return "";
    }
    return a === b ? a : `${a} – ${b}`;
}

function truncateLabel(str, maxLen) {
    const s = String(str ?? "").trim() || "—";
    if (s.length <= maxLen) {
        return s;
    }
    return `${s.slice(0, Math.max(0, maxLen - 1))}…`;
}

/** @typedef {{ column: string | null, step: 0 | 1 | 2 }} MarketingTableSortState */

const MARKETING_SORT_LOCALE = "de";

const CHANNEL_SORT_KEYS = {
    channel: "text",
    campaignCount: "number",
    consents: "number",
    acceptPct: "number",
    acceptAll: "number",
    essentialOnly: "number",
    granular: "number",
};

const CAMPAIGN_SORT_KEYS = {
    utmCampaign: "text",
    consents: "number",
    acceptPct: "number",
    acceptAll: "number",
    essentialOnly: "number",
    granular: "number",
};

function defaultChannelOverviewOrder(a, b) {
    const c = b.consents - a.consents;
    if (c !== 0) return c;
    return String(a.channel).localeCompare(String(b.channel), MARKETING_SORT_LOCALE, { sensitivity: "base" });
}

function defaultCampaignRowOrder(a, b) {
    const c = b.consents - a.consents;
    if (c !== 0) return c;
    return String(a.utmCampaign).localeCompare(String(b.utmCampaign), MARKETING_SORT_LOCALE, { sensitivity: "base" });
}

function cmpNullableFiniteNumber(a, b, desc) {
    const na = a == null || !Number.isFinite(a);
    const nb = b == null || !Number.isFinite(b);
    if (na && nb) return 0;
    if (na) return 1;
    if (nb) return -1;
    return desc ? b - a : a - b;
}

function channelRowNumberValue(row, column) {
    console.log(row, column);
    switch (column) {
        case "campaignCount":
            return row.campaignCount;
        case "consents":
            return row.consents;
        case "acceptPct":
            return row.acceptPct;
        case "acceptAll":
            return row.acceptAll;
        case "essentialOnly":
            return row.essentialOnly;
        case "granular":
            return row.granular;
        default:
            return 0;
    }
}

function campaignRowNumberValue(row, column) {
    switch (column) {
        case "consents":
            return row.consents;
        case "acceptPct":
            return row.acceptPct;
        case "acceptAll":
            return row.acceptAll;
        case "essentialOnly":
            return row.essentialOnly;
        case "granular":
            return row.granular;
        default:
            return 0;
    }
}

/** @param {MarketingTableSortState} sort */
function sortChannelOverviewRows(rows, sort) {
    const list = [...rows];
    if (!sort.column || sort.step === 0) {
        list.sort(defaultChannelOverviewOrder);
        return list;
    }
    const kind = CHANNEL_SORT_KEYS[sort.column];
    if (!kind) {
        list.sort(defaultChannelOverviewOrder);
        return list;
    }
    if (kind === "text" && sort.column === "channel") {
        const desc = sort.step === 2;
        list.sort((a, b) => {
            const cmp = String(a.channel).localeCompare(String(b.channel), MARKETING_SORT_LOCALE, {
                sensitivity: "base",
            });
            if (cmp !== 0) return desc ? -cmp : cmp;
            return defaultChannelOverviewOrder(a, b);
        });
        return list;
    }
    if (kind === "number") {
        const desc = sort.step === 1;
        list.sort((a, b) => {
            const va = channelRowNumberValue(a, sort.column);
            const vb = channelRowNumberValue(b, sort.column);
            if (sort.column === "acceptPct") {
                const c = cmpNullableFiniteNumber(va, vb, desc);
                if (c !== 0) return c;
            } else {
                const na = Number(va) || 0;
                const nb = Number(vb) || 0;
                if (na !== nb) return desc ? nb - na : na - nb;
            }
            return defaultChannelOverviewOrder(a, b);
        });
        return list;
    }
    list.sort(defaultChannelOverviewOrder);
    return list;
}

function sortCampaignDrilldownRows(rows, sort) {
    const list = [...rows];
    if (!sort.column || sort.step === 0) {
        list.sort(defaultCampaignRowOrder);
        return list;
    }
    const kind = CAMPAIGN_SORT_KEYS[sort.column];
    if (!kind) {
        list.sort(defaultCampaignRowOrder);
        return list;
    }
    if (kind === "text" && sort.column === "utmCampaign") {
        const desc = sort.step === 2;
        list.sort((a, b) => {
            const cmp = String(a.utmCampaign).localeCompare(String(b.utmCampaign), MARKETING_SORT_LOCALE, {
                sensitivity: "base",
            });
            if (cmp !== 0) return desc ? -cmp : cmp;
            return defaultCampaignRowOrder(a, b);
        });
        return list;
    }
    if (kind === "number") {
        const desc = sort.step === 1;
        list.sort((a, b) => {
            const va = campaignRowNumberValue(a, sort.column);
            const vb = campaignRowNumberValue(b, sort.column);
            if (sort.column === "acceptPct") {
                const c = cmpNullableFiniteNumber(va, vb, desc);
                if (c !== 0) return c;
            } else {
                const na = Number(va) || 0;
                const nb = Number(vb) || 0;
                if (na !== nb) return desc ? nb - na : na - nb;
            }
            return defaultCampaignRowOrder(a, b);
        });
        return list;
    }
    list.sort(defaultCampaignRowOrder);
    return list;
}

function cycleMarketingTableSort(prev, columnKey) {
    if (prev.column !== columnKey) {
        return { column: columnKey, step: 1 };
    }
    if (prev.step >= 2) {
        return { column: null, step: 0 };
    }
    return { column: columnKey, step: (prev.step + 1) };
}

function marketingSortButtonGlyph(columnKey, kind, sort) {
    const active = sort.column === columnKey && sort.step > 0;
    if (!active) {
        return "↕";
    }
    if (kind === "text") {
        return sort.step === 1 ? "A→Z" : "Z→A";
    }
    return sort.step === 1 ? "9→1" : "1→9";
}

function marketingSortAriaLabel(label, columnKey, kind, sort) {
    const active = sort.column === columnKey && sort.step > 0;
    if (!active) {
        return `Sort ${label}: default (by consents). Activate for alphabetic or amount order.`;
    }
    if (kind === "text") {
        if (sort.step === 1) {
            return `Sort ${label}: A to Z. Press again for Z to A, then default.`;
        }
        return `Sort ${label}: Z to A. Press again for default.`;
    }
    if (sort.step === 1) {
        return `Sort ${label}: highest to lowest. Press again for lowest to highest, then default.`;
    }
    return `Sort ${label}: lowest to highest. Press again for default.`;
}

/*
 * Flip the up/down class when lowerIsBetter: for metrics like
 * "analytics-invisible traffic", an increase is a regression. The text
 * (the percent-change value itself) stays the same — only the color/
 * arrow semantics are inverted so green always means "good".
 */
function invertTrendClassIfNeeded(cls, lowerIsBetter) {
    if (!lowerIsBetter) return cls;
    if (cls === "marketing-report-delta--up") return "marketing-report-delta--down";
    if (cls === "marketing-report-delta--down") return "marketing-report-delta--up";
    return cls;
}

function MarketingCompareInline({ current, previous, kind = "volume", lowerIsBetter = false }) {
    if (kind === "rate") {
        const c = Number(current);
        const hasCur = current != null && Number.isFinite(c);
        if (previous == null && !hasCur) return null;
    }
    if (previous == null) {
        return (
            <span
                className="marketing-report-delta marketing-report-delta--new"
                title="No matching attribution row in the comparison window"
            >
                new
            </span>
        );
    }
    if (kind === "rate") {
        const pts = formatPtsChange(current, previous);
        if (pts == null) return null;
        const c = Number(current);
        const p = Number(previous);
        const rawCls =
            Number.isFinite(c) && Number.isFinite(p)
                ? c > p
                    ? "marketing-report-delta--up"
                    : c < p
                      ? "marketing-report-delta--down"
                      : "marketing-report-delta--flat"
                : "";
        const cls = invertTrendClassIfNeeded(rawCls, lowerIsBetter);
        return (
            <span className={["marketing-report-delta", cls].filter(Boolean).join(" ")} title="Change vs comparison window (points)">
                {pts}
            </span>
        );
    }
    const rel = formatPctChange(current, previous);
    if (rel == null) return null;
    const rawCls = marketingCompareVolumeClass(current, previous);
    const cls = invertTrendClassIfNeeded(rawCls, lowerIsBetter);
    return (
        <span
            className={["marketing-report-delta", cls].filter(Boolean).join(" ")}
            title="Change vs comparison window"
        >
            {rel}
        </span>
    );
}

function MarketingMetricStack({ compareUi, primary, current, previous, kind = "volume", lowerIsBetter = false }) {
    return (
        <div className="marketing-report-cell-stack">
            <span className="marketing-report-cell-stack__primary">{primary}</span>
            {compareUi ? (
                <span className="marketing-report-cell-stack__compare">
                    <MarketingCompareInline
                        kind={kind}
                        current={current}
                        previous={previous}
                        lowerIsBetter={lowerIsBetter}
                    />
                </span>
            ) : null}
        </div>
    );
}

function MarketingTableSortTh({ label, columnKey, kind, sortState, onCycle, className }) {
    const glyph = marketingSortButtonGlyph(columnKey, kind, sortState);
    const active = sortState.column === columnKey && sortState.step > 0;
    return (
        <th className={className}>
            <span className="marketing-report-th-inner">
                <span className="marketing-report-th-label">{label}</span>
                <button
                    type="button"
                    className={`marketing-report-col-sort${active ? " marketing-report-col-sort--active" : ""}`}
                    aria-label={marketingSortAriaLabel(label, columnKey, kind, sortState)}
                    title={marketingSortAriaLabel(label, columnKey, kind, sortState)}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onCycle(columnKey);
                    }}
                >
                    {glyph}
                </button>
            </span>
        </th>
    );
}

const CHANNEL_TABLE_PAGE_SIZE = 10;

/**
 * Build a compact list of page numbers with "…" gaps for the pagination nav.
 * Always keeps the first page, last page, current page, and its two neighbours
 * visible so the user can jump to edges without scanning 20+ buttons.
 */
function buildMarketingPageNumbers(current, total) {
    if (total <= 7) {
        const list = [];
        for (let i = 1; i <= total; i += 1) list.push(i);
        return list;
    }
    const keep = new Set([1, total, current, current - 1, current + 1]);
    const sorted = [...keep]
        .filter((p) => p >= 1 && p <= total)
        .sort((a, b) => a - b);
    const result = [];
    let prev = 0;
    for (const p of sorted) {
        if (p - prev > 1) result.push("gap");
        result.push(p);
        prev = p;
    }
    return result;
}

function MarketingTablePagination({ page, pageCount, totalRows, pageSize, onPageChange, label = "channels" }) {
    if (pageCount <= 1) return null;
    const goTo = (p) => {
        const safe = Math.max(1, Math.min(pageCount, p));
        if (safe !== page) onPageChange(safe);
    };
    const firstRow = (page - 1) * pageSize + 1;
    const lastRow = Math.min(totalRows, page * pageSize);
    const pageNumbers = buildMarketingPageNumbers(page, pageCount);
    return (
        <nav className="marketing-report-pagination" aria-label={`${label} pagination`}>
            <button
                type="button"
                className="marketing-report-pagination__btn"
                onClick={() => goTo(page - 1)}
                disabled={page <= 1}
                aria-label="Previous page"
            >
                ← Previous
            </button>
            <ul className="marketing-report-pagination__pages">
                {pageNumbers.map((p, i) =>
                    p === "gap" ? (
                        <li
                            key={`gap-${i}`}
                            className="marketing-report-pagination__gap"
                            aria-hidden="true"
                        >
                            …
                        </li>
                    ) : (
                        <li key={p}>
                            <button
                                type="button"
                                className={`marketing-report-pagination__page${
                                    p === page ? " marketing-report-pagination__page--active" : ""
                                }`}
                                onClick={() => goTo(p)}
                                aria-current={p === page ? "page" : undefined}
                                aria-label={`Page ${p}`}
                            >
                                {p}
                            </button>
                        </li>
                    )
                )}
            </ul>
            <button
                type="button"
                className="marketing-report-pagination__btn"
                onClick={() => goTo(page + 1)}
                disabled={page >= pageCount}
                aria-label="Next page"
            >
                Next →
            </button>
            <span className="marketing-report-pagination__meta">
                Showing <strong>{firstRow.toLocaleString("de-DE")}</strong>
                –<strong>{lastRow.toLocaleString("de-DE")}</strong> of{" "}
                <strong>{totalRows.toLocaleString("de-DE")}</strong> {label}
            </span>
        </nav>
    );
}

/**
 * Marketer-first signals: volume, acceptance, choice mix, data gaps.
 */
function computeMarketingHighlights({
    selectedChannel,
    channelOverview,
    drilldownRows,
    totalConsents,
    drillConsents,
    unclassifiedConsents,
    fromDate,
    toDate,
}) {
    const period = formatPeriodRange(fromDate, toDate);
    const periodPhrase = period ? `For ${period}` : "For your selected dates";

    if (selectedChannel) {
        const total = drillConsents;
        const nCamp = drilldownRows.length;
        const headline = `${total.toLocaleString("de-DE")} consents · ${nCamp} campaign${nCamp === 1 ? "" : "s"}`;
        const subline = `${selectedChannel} · ${periodPhrase.toLowerCase()}. Compare campaigns, then use Performance context for geography and paths.`;
        const items = [];
        if (nCamp === 0) {
            return {
                eyebrow: "Here's what to care about in this channel",
                headline,
                subline,
                items: [],
            };
        }

        const sorted = [...drilldownRows].sort((a, b) => b.consents - a.consents);
        const topCamp = sorted[0];
        const share = total > 0 ? Math.round((topCamp.consents / total) * 1000) / 10 : 0;
        items.push({
            accent: "spotlight",
            title: "Campaign carrying the load",
            body: `"${truncateLabel(topCamp.utmCampaign, 64)}" represents about ${share}% of this channel (${topCamp.consents.toLocaleString("de-DE")} events).`,
        });

        const minC = Math.max(5, Math.floor(total * 0.05));
        const withRate = sorted.filter(
            (r) => r.consents >= minC && r.acceptPct != null && Number.isFinite(r.acceptPct)
        );
        if (withRate.length >= 2) {
            const best = [...withRate].sort((a, b) => b.acceptPct - a.acceptPct)[0];
            const worst = [...withRate].sort((a, b) => a.acceptPct - b.acceptPct)[0];
            items.push({
                accent: "win",
                title: "Strongest acceptance",
                body: `"${truncateLabel(best.utmCampaign, 56)}" leads at ${best.acceptPct.toLocaleString("de-DE", { maximumFractionDigits: 1 })}% among campaigns with enough volume to compare.`,
            });
            if (worst.utmCampaign !== best.utmCampaign) {
                items.push({
                    accent: "watch",
                    title: "Review next",
                    body: `"${truncateLabel(worst.utmCampaign, 56)}" is lowest in that set (${worst.acceptPct.toLocaleString("de-DE", { maximumFractionDigits: 1 })}%)—worth checking creative, landing, or consent timing.`,
                });
            }
        } else if (withRate.length === 1) {
            const only = withRate[0];
            items.push({
                accent: "data",
                title: "Acceptance",
                body: `One campaign clears the volume bar: ${only.acceptPct.toLocaleString("de-DE", { maximumFractionDigits: 1 })}% on "${truncateLabel(only.utmCampaign, 48)}".`,
            });
        }

        const merged = mergeAllContext(drilldownRows);
        if (merged.topCountries.length > 0) {
            const c = merged.topCountries[0];
            items.push({
                accent: "data",
                title: "Top geography (merged)",
                body: `${c.id} shows the most volume in the slices returned (${c.consents.toLocaleString("de-DE")} consents). Expand Performance context for the full list.`,
            });
        }

        return {
            eyebrow: "Here's what to care about in this channel",
            headline,
            subline,
            items: items.slice(0, 5),
        };
    }

    if (channelOverview.length === 0) {
        return {
            eyebrow: "Here's what to care about today",
            headline: "No attributed marketing traffic in this window yet",
            subline: `${periodPhrase}. Widen the range, switch domain scope, or confirm UTMs on landing URLs.`,
            items: [],
        };
    }

    const headline = `${totalConsents.toLocaleString("de-DE")} tagged consent events · ${channelOverview.length} channel${channelOverview.length === 1 ? "" : "s"}`;
    const subline = `${periodPhrase}. Scan highlights first, then open a channel to compare campaigns.`;
    const items = [];
    const topCh = channelOverview[0];
    if (totalConsents > 0 && topCh) {
        const pct = Math.round((topCh.consents / totalConsents) * 1000) / 10;
        items.push({
            accent: "spotlight",
            title: "Where volume concentrates",
            body: `${topCh.channel} drives about ${pct}% of consents (${topCh.consents.toLocaleString("de-DE")} events)—often the first place to optimize spend and messaging.`,
        });
    }

    const minCh = Math.max(10, Math.floor(totalConsents * 0.04));
    const chWithRate = channelOverview.filter(
        (c) => c.consents >= minCh && c.acceptPct != null && Number.isFinite(c.acceptPct)
    );
    if (chWithRate.length >= 2) {
        const best = [...chWithRate].sort((a, b) => b.acceptPct - a.acceptPct)[0];
        const worst = [...chWithRate].sort((a, b) => a.acceptPct - b.acceptPct)[0];
        items.push({
            accent: "win",
            title: "Best acceptance among larger channels",
            body: `${best.channel} leads at ${best.acceptPct.toLocaleString("de-DE", { maximumFractionDigits: 1 })}% weighted acceptance (channels above a small volume floor).`,
        });
        if (worst.channel !== best.channel) {
            items.push({
                accent: "watch",
                title: "Softest signal in that set",
                body: `${worst.channel} is lowest at ${worst.acceptPct.toLocaleString("de-DE", { maximumFractionDigits: 1 })}%—creative, landing page, or banner timing may deserve a pass.`,
            });
        }
    } else if (chWithRate.length === 1) {
        const only = chWithRate[0];
        items.push({
            accent: "data",
            title: "Acceptance snapshot",
            body: `With current volume, ${only.channel} is the main comparable signal: ${only.acceptPct.toLocaleString("de-DE", { maximumFractionDigits: 1 })}% weighted acceptance.`,
        });
    }

    const topEssential = [...channelOverview]
        .map((c) => ({
            ...c,
            essentialPct: c.consents > 0 ? (c.essentialOnly / c.consents) * 100 : 0,
        }))
        .sort((a, b) => b.essentialPct - a.essentialPct)[0];
    if (topEssential && topEssential.consents >= minCh && topEssential.essentialPct >= 18) {
        items.push({
            accent: "watch",
            title: "Necessary-only lean",
            body: `${topEssential.channel}: ${Math.round(topEssential.essentialPct * 10) / 10}% chose essential cookies only—align tags and analytics with how people actually consent.`,
        });
    }

    if (unclassifiedConsents > 0) {
        items.push({
            accent: "data",
            title: "Incomplete choice data",
            body: `${unclassifiedConsents.toLocaleString("de-DE")} events could not be split into accept / essential / granular; consent totals still include them.`,
        });
    }

    return {
        eyebrow: "Here's what to care about today",
        headline,
        subline,
        items: items.slice(0, 5),
    };
}

function MarketingHighlightsSection({ highlights }) {
    if (!highlights) {
        return null;
    }
    return (
        <section className="marketing-highlights" aria-labelledby="marketing-highlights-heading">
            <h2 id="marketing-highlights-heading" className="marketing-report-section__title marketing-highlights__h2">
                Highlights
            </h2>
            <p className="marketing-highlights__eyebrow">{highlights.eyebrow}</p>
            <p className="marketing-highlights__headline">{highlights.headline}</p>
            <p className="marketing-highlights__sub">{highlights.subline}</p>
            {highlights.items.length > 0 ? (
                <ul className="marketing-highlights__list">
                    {highlights.items.map((it, i) => (
                        <li
                            key={`${it.title}-${i}`}
                            className={`marketing-highlights__item marketing-highlights__item--${it.accent}`}
                        >
                            <span className="marketing-highlights__item-title">{it.title}</span>
                            <p className="marketing-highlights__item-body">{it.body}</p>
                        </li>
                    ))}
                </ul>
            ) : null}
        </section>
    );
}

const _mrResolveCache = new Map();

function mrIsNumericId(val) {
    return val && /^\d{5,}$/.test(String(val).trim());
}

function channelToPlatform(channel) {
    const c = (channel || "").toLowerCase();
    if (c.includes("google ads")) return "google_ads";
    if (c.includes("facebook ads") || c.includes("instagram ads")) return "meta_ads";
    if (c.includes("microsoft ads")) return "microsoft_ads";
    return null;
}

export default function MarketingReport() {
    document.title = "Marketing | Analytics | Intastellar Consents";
    const [currentDomain, setGlobalDomain] = useContext(DomainContext);
    // Reached from /analytics/:handle/marketing, which carries no platform
    // :id — fall back to the platform stashed at login time, same as the
    // header and main nav do for other analytics-scoped routes.
    const { id: routeId, handle } = useParams();
    const id = routeId || localStorage.getItem("platform") || "gdpr";
    useSyncDomainFromRoute(handle, setGlobalDomain);

    const settings = (() => {
        try {
            return JSON.parse(localStorage.getItem("settings")) || { dateRange: 30 };
        } catch {
            return { dateRange: 30 };
        }
    })();

    const today = new Date();
    const initialLastDays =
        localStorage.getItem("settings") != null ? JSON.parse(localStorage.getItem("settings")).dateRange : 30;
    const [getLastDays, setLastDays] = useState(initialLastDays);
    const [fromDate, setFromDate] = useState(
        new Date(new Date().setDate(today.getDate() - (settings?.dateRange ?? 30)))
    );
    const [toDate, setToDate] = useState(new Date(new Date().setDate(today.getDate() - 1)));
    const [, setActiveData] = useState(null);
    const [compareRange, setCompareRange] = useState(0);
    const [previousPeriod, setPreviousPeriod] = useState(() =>
        defaultCompareWindowForPrimary(
            new Date(new Date().setDate(new Date().getDate() - (settings?.dateRange ?? 30))),
            new Date(new Date().setDate(new Date().getDate() - 1))
        ).start
    );
    const [previousPeriod2, setPreviousPeriod2] = useState(() =>
        defaultCompareWindowForPrimary(
            new Date(new Date().setDate(new Date().getDate() - (settings?.dateRange ?? 30))),
            new Date(new Date().setDate(new Date().getDate() - 1))
        ).end
    );

    const listDomainLabel = useMemo(
        () => consentsDomainFromRoute(handle, currentDomain),
        [handle, currentDomain]
    );
    const domainsApiHeader = useMemo(() => toDomainsApiHeader(listDomainLabel), [listDomainLabel]);

    const [rows, setRows] = useState([]);
    /** Raw baseline window rows (same shape as primary) when period comparison is on. */
    const [baselineRows, setBaselineRows] = useState([]);
    const [baselineSummary, setBaselineSummary] = useState(null);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [compareBaselineNote, setCompareBaselineNote] = useState(null);
    /** Level 2: which channel’s campaigns are shown; null = channel overview. */
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [channelTableSort, setChannelTableSort] = useState({ column: null, step: 0 });
    const [campaignTableSort, setCampaignTableSort] = useState({ column: null, step: 0 });
    const [channelTablePage, setChannelTablePage] = useState(1);
    const [resolvedMrCampaigns, setResolvedMrCampaigns] = useState({});
    const mrResolvingRef = useRef(new Set());

    /*
     * Phase 2: daily time-series payload for the channel Line chart.
     * Fetched alongside the aggregated marketing report — one call per
     * window, reused for every channel drill-down. The fetch is
     * deliberately best-effort: if the endpoint is missing or the
     * response isn't JSON, we keep the old dashboard fully functional
     * and surface a friendly note in the chart card instead of an error.
     */
    const [timeseriesRows, setTimeseriesRows] = useState([]);
    const [baselineTimeseriesRows, setBaselineTimeseriesRows] = useState([]);
    const [timeseriesLoading, setTimeseriesLoading] = useState(false);
    const [timeseriesError, setTimeseriesError] = useState(null);

    // Only the aggregate summary is kept here — the full GA4 sessions/
    // channel-breakdown chart moved to its own Analytics page (Ga4SessionsChart,
    // now in src/Pages/Analytics/GoogleAnalyticsChart.js). This page still needs
    // ga4Summary.sessions for the lightweight "GA4 consent coverage" / "dark
    // zone" highlight cards below, which are specific to this reconciliation view.
    const [ga4Summary, setGa4Summary] = useState(null);
    const [adPerfData, setAdPerfData] = useState(null);
    const [adPerfLoading, setAdPerfLoading] = useState(false);

    const endpoint = API[id]?.marketingAttribution;
    const timeseriesEndpoint = API[id]?.marketingAttributionTimeseries;

    const fetchReport = useCallback(async () => {
        if (!endpoint?.url) {
            setError("Marketing is not configured for this platform.");
            setRows([]);
            setBaselineRows([]);
            setSummary(null);
            setBaselineSummary(null);
            return;
        }
        setLoading(true);
        setError(null);
        setCompareBaselineNote(null);
        try {
            const compareOn = compareRangeActive(compareRange);
            const baseHeaders = { ...endpoint.headers, Domains: domainsApiHeader };
            const primaryHeaders = {
                ...baseHeaders,
                FromDate: toYmd(fromDate),
                ToDate: toYmd(toDate),
            };
            if (compareOn) {
                primaryHeaders.CompareRange =
                    compareRange === "Same period last year" ? "Same period last year" : String(compareRange);
                primaryHeaders.PreviousPeriod = toYmd(previousPeriod);
                primaryHeaders.PreviousPeriod2 = toYmd(previousPeriod2);
                primaryHeaders["X-Compare-Start"] = toYmd(previousPeriod);
                primaryHeaders["X-Compare-End"] = toYmd(previousPeriod2);
                primaryHeaders["X-Compare-Range"] = primaryHeaders.CompareRange;
            } else {
                primaryHeaders.CompareRange = "";
                primaryHeaders.PreviousPeriod = "";
                primaryHeaders.PreviousPeriod2 = "";
                primaryHeaders["X-Compare-Start"] = "";
                primaryHeaders["X-Compare-End"] = "";
                primaryHeaders["X-Compare-Range"] = "";
            }

            const baselineHeaders = {
                ...baseHeaders,
                FromDate: toYmd(previousPeriod),
                ToDate: toYmd(previousPeriod2),
                CompareRange: "",
                PreviousPeriod: "",
                PreviousPeriod2: "",
                "X-Compare-Start": "",
                "X-Compare-End": "",
                "X-Compare-Range": "",
            };

            const reqMethod = endpoint.method || "GET";
            const primaryFetch = fetch(endpoint.url, { method: reqMethod, headers: primaryHeaders });
            const fetches = compareOn
                ? [primaryFetch, fetch(endpoint.url, { method: reqMethod, headers: baselineHeaders })]
                : [primaryFetch];
            const responses = await Promise.all(fetches);
            const res = responses[0];
            const resBaseline = compareOn ? responses[1] : null;

            const text = await res.text();
            let json = null;
            try {
                json = text ? JSON.parse(text) : null;
            } catch {
                setError("The server returned a non-JSON response.");
                setRows([]);
                setBaselineRows([]);
                setSummary(null);
                setBaselineSummary(null);
                return;
            }
            if (!res.ok) {
                setError(json?.message || `Request failed (${res.status}).`);
                setRows([]);
                setBaselineRows([]);
                setSummary(null);
                setBaselineSummary(null);
                return;
            }
            if (json === "Err_Login_Expired") {
                appStorage.removeItem("globals");
                window.location.href = "/login";
                return;
            }

            let baselineMapped = [];
            let baselineSummaryNext = null;
            if (compareOn && resBaseline) {
                const textB = await resBaseline.text();
                let jsonB = null;
                try {
                    jsonB = textB ? JSON.parse(textB) : null;
                } catch {
                    setCompareBaselineNote("Comparison window returned non-JSON data.");
                }
                if (jsonB === "Err_Login_Expired") {
                    appStorage.removeItem("globals");
                    window.location.href = "/login";
                    return;
                }
                if (!resBaseline.ok) {
                    setCompareBaselineNote(jsonB?.message || `Comparison request failed (${resBaseline.status}).`);
                } else if (jsonB != null) {
                    baselineMapped = extractRows(jsonB).map(mapRow);
                    baselineSummaryNext = extractSummary(jsonB);
                }
            }

            const rawRows = extractRows(json);
            const primaryMapped = rawRows.map(mapRow).sort((a, b) => b.consents - a.consents);
            const merged = attachBaselineToRows(primaryMapped, compareOn ? baselineMapped : []);
            setRows(merged);
            setBaselineRows(compareOn ? baselineMapped : []);
            setSummary(extractSummary(json));
            setBaselineSummary(compareOn ? baselineSummaryNext : null);
            setSelectedChannel(null);
        } catch (e) {
            setError(e?.message || "Network error while loading marketing data.");
            setRows([]);
            setBaselineRows([]);
            setSummary(null);
            setBaselineSummary(null);
        } finally {
            setLoading(false);
        }
    }, [
        endpoint,
        domainsApiHeader,
        fromDate,
        toDate,
        compareRange,
        previousPeriod,
        previousPeriod2,
    ]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    /*
     * Fetch the daily time-series for the current window. Runs in
     * parallel to the main report (independent state), so a slow or
     * failing timeseries request never blocks the aggregated view.
     */
    useEffect(() => {
        let cancelled = false;
        async function loadTimeseries() {
            if (!timeseriesEndpoint?.url) {
                setTimeseriesRows([]);
                setBaselineTimeseriesRows([]);
                setTimeseriesError(null);
                return;
            }
            setTimeseriesLoading(true);
            setTimeseriesError(null);
            try {
                const compareOnLocal = compareRangeActive(compareRange);
                const baseHeaders = { ...timeseriesEndpoint.headers, Domains: domainsApiHeader };
                const headers = {
                    ...baseHeaders,
                    FromDate: toYmd(fromDate),
                    ToDate: toYmd(toDate),
                    CompareRange: compareOnLocal
                        ? compareRange === "Same period last year"
                            ? "Same period last year"
                            : String(compareRange)
                        : "",
                    PreviousPeriod: compareOnLocal ? toYmd(previousPeriod) : "",
                    PreviousPeriod2: compareOnLocal ? toYmd(previousPeriod2) : "",
                    "X-Compare-Start": compareOnLocal ? toYmd(previousPeriod) : "",
                    "X-Compare-End": compareOnLocal ? toYmd(previousPeriod2) : "",
                    "X-Compare-Range": compareOnLocal
                        ? compareRange === "Same period last year"
                            ? "Same period last year"
                            : String(compareRange)
                        : "",
                };
                const res = await fetch(timeseriesEndpoint.url, {
                    method: timeseriesEndpoint.method || "GET",
                    headers,
                });
                if (cancelled) return;
                if (!res.ok) {
                    setTimeseriesRows([]);
                    setBaselineTimeseriesRows([]);
                    setTimeseriesError(
                        res.status === 404
                            ? "Daily trend endpoint not available yet."
                            : `Daily trend request failed (${res.status}).`
                    );
                    return;
                }
                const text = await res.text();
                let json = null;
                try {
                    json = text ? JSON.parse(text) : null;
                } catch {
                    setTimeseriesRows([]);
                    setBaselineTimeseriesRows([]);
                    setTimeseriesError("Daily trend response was not JSON.");
                    return;
                }
                if (json === "Err_Login_Expired") {
                    appStorage.removeItem("globals");
                    window.location.href = "/login";
                    return;
                }
                const ts = Array.isArray(json?.timeseries) ? json.timeseries : [];
                const baseline = Array.isArray(json?.compareTimeseries) ? json.compareTimeseries : [];
                setTimeseriesRows(ts);
                setBaselineTimeseriesRows(compareOnLocal ? baseline : []);
                setTimeseriesError(null);
            } catch (e) {
                if (cancelled) return;
                setTimeseriesRows([]);
                setBaselineTimeseriesRows([]);
                setTimeseriesError(e?.message || "Network error while loading daily trend.");
            } finally {
                if (!cancelled) setTimeseriesLoading(false);
            }
        }
        loadTimeseries();
        return () => {
            cancelled = true;
        };
    }, [
        timeseriesEndpoint,
        domainsApiHeader,
        fromDate,
        toDate,
        compareRange,
        previousPeriod,
        previousPeriod2,
    ]);

    // Fetch GA4 daily sessions when a GA4 connection exists for this domain
    useEffect(() => {
        const domain = listDomainLabel;
        if (!domain || domain === "combined view") return;
        const authToken = Authentication.getToken();
        const orgId = Authentication.getOrganisation();
        if (!authToken || !orgId) return;
        const fromYmd = toYmd(fromDate);
        const toYmd2  = toYmd(toDate);
        if (!fromYmd || !toYmd2) return;

        let cancelled = false;
        setGa4Summary(null);

        const headers = { Authorization: authToken, Organisation: String(orgId) };

        // Check if GA4 is connected for this domain, then fetch its summary
        fetch(`${ScannerHost}/api/ad-connections?domain=${encodeURIComponent(domain)}`, { headers })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (cancelled) return;
                const hasGa4 = (data?.connections || []).some(
                    c => c.platform === "google_analytics" && c.account_id
                );
                if (!hasGa4) return;
                const qs = `platform=google_analytics&domain=${encodeURIComponent(domain)}&fromDate=${fromYmd}&toDate=${toYmd2}`;
                return fetch(`${ScannerHost}/api/ad-daily-data?${qs}`, { headers })
                    .then(r => r.ok ? r.json() : null)
                    .then(daily => {
                        if (cancelled) return;
                        if (daily?.summary) setGa4Summary(daily.summary);
                    });
            })
            .catch(() => {});

        return () => { cancelled = true; };
    }, [listDomainLabel, fromDate, toDate]);

    // Fetch aggregated ad platform data (spend / clicks / impressions) for the
    // current domain + date range. Used to show the "Ad Performance" panel on
    // the channel overview. Silently skipped for the combined view and when no
    // ad platforms are connected.
    useEffect(() => {
        const domain = listDomainLabel;
        if (!domain || domain === "combined view") return;
        const authToken = Authentication.getToken();
        const orgId = Authentication.getOrganisation();
        if (!authToken || !orgId) return;
        const fromYmd = toYmd(fromDate);
        const toYmd2  = toYmd(toDate);
        if (!fromYmd || !toYmd2) return;

        let cancelled = false;
        setAdPerfData(null);
        setAdPerfLoading(true);

        const qs = new URLSearchParams({ from: fromYmd, to: toYmd2 }).toString();
        fetch(`${ScannerHost}/api/ad-spend-report?${qs}`, {
            headers: {
                Authorization: authToken,
                Organisation: String(orgId),
                Domains: toDomainsApiHeader(domain),
            },
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (cancelled) return;
                if (data && !data.noConnections && data.platforms?.length > 0) setAdPerfData(data);
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setAdPerfLoading(false); });

        return () => { cancelled = true; };
    }, [listDomainLabel, fromDate, toDate]);

    const compareOn = useMemo(() => compareRangeActive(compareRange), [compareRange]);
    const compareUi = compareOn && !compareBaselineNote;

    const totalConsents = useMemo(
        () => pickTotalConsentsFromSummary(summary, rows),
        [summary, rows]
    );

    const totalBaselineConsents = useMemo(() => {
        if (!compareUi) return null;
        return pickTotalConsentsFromSummary(baselineSummary, baselineRows);
    }, [compareUi, baselineSummary, baselineRows]);

    const channelOverview = useMemo(() => buildChannelOverview(rows), [rows]);

    const channelOverviewWithCompare = useMemo(
        () => mergeChannelOverviewWithBaseline(channelOverview, compareUi ? baselineRows : []),
        [channelOverview, baselineRows, compareUi]
    );

    const drilldownRows = useMemo(() => {
        if (!selectedChannel) return [];
        return rows.filter((r) => r.channel === selectedChannel);
    }, [rows, selectedChannel]);

    useEffect(() => {
        const domain = listDomainLabel;
        const platform = channelToPlatform(selectedChannel);
        if (!platform || !domain || domain === "combined view" || !drilldownRows.length) return;
        const authToken = Authentication.getToken();
        const orgId = Authentication.getOrganisation();
        if (!authToken || !orgId) return;
        const toFetch = [];
        for (const r of drilldownRows) {
            if (!mrIsNumericId(r.utmCampaign)) continue;
            const key = `${platform}:${r.utmCampaign}`;
            if (!_mrResolveCache.has(key) && !mrResolvingRef.current.has(key)) toFetch.push({ key, platform, id: r.utmCampaign });
        }
        const unique = [...new Map(toFetch.map(x => [x.key, x])).values()];
        if (!unique.length) {
            const hit = {};
            for (const r of drilldownRows) {
                const key = `${platform}:${r.utmCampaign}`;
                if (_mrResolveCache.has(key)) hit[key] = _mrResolveCache.get(key);
            }
            if (Object.keys(hit).length) setResolvedMrCampaigns(prev => ({ ...prev, ...hit }));
            return;
        }
        for (const { key } of unique) mrResolvingRef.current.add(key);
        Promise.all(unique.map(async ({ key, platform: p, id }) => {
            try {
                const r = await fetch(
                    `${ScannerHost}/api/ad-id-resolve?platform=${p}&id=${encodeURIComponent(id)}&domain=${encodeURIComponent(domain)}`,
                    { headers: { Authorization: authToken, Organisation: String(orgId) } }
                );
                if (r.ok) {
                    const json = await r.json();
                    _mrResolveCache.set(key, json);
                    return [key, json];
                }
            } catch { /* ignore */ }
            return null;
        })).then(results => {
            const updates = {};
            for (const r of results) if (r) updates[r[0]] = r[1];
            if (Object.keys(updates).length) setResolvedMrCampaigns(prev => ({ ...prev, ...updates }));
            for (const { key } of unique) mrResolvingRef.current.delete(key);
        });
    }, [drilldownRows, selectedChannel, listDomainLabel]);

    const sortedChannelOverview = useMemo(
        () => sortChannelOverviewRows(channelOverviewWithCompare, channelTableSort),
        [channelOverviewWithCompare, channelTableSort]
    );

    const channelTablePageCount = Math.max(
        1,
        Math.ceil(sortedChannelOverview.length / CHANNEL_TABLE_PAGE_SIZE)
    );

    // Reset to page 1 whenever the sort changes so the user doesn't land on an
    // empty/irrelevant page after re-ordering the table.
    useEffect(() => {
        setChannelTablePage(1);
    }, [channelTableSort]);

    // Clamp the current page if the underlying data shrinks (e.g. a date filter
    // drops the channel count from 23 to 4).
    useEffect(() => {
        setChannelTablePage((prev) => Math.min(Math.max(1, prev), channelTablePageCount));
    }, [channelTablePageCount]);

    const pagedChannelOverview = useMemo(() => {
        const start = (channelTablePage - 1) * CHANNEL_TABLE_PAGE_SIZE;
        return sortedChannelOverview.slice(start, start + CHANNEL_TABLE_PAGE_SIZE);
    }, [sortedChannelOverview, channelTablePage]);

    const sortedDrilldownRows = useMemo(
        () => sortCampaignDrilldownRows(drilldownRows, campaignTableSort),
        [drilldownRows, campaignTableSort]
    );

    const cycleChannelTableSort = useCallback((columnKey) => {
        setChannelTableSort((prev) => cycleMarketingTableSort(prev, columnKey));
    }, []);

    const cycleCampaignTableSort = useCallback((columnKey) => {
        setCampaignTableSort((prev) => cycleMarketingTableSort(prev, columnKey));
    }, []);

    const drillConsents = useMemo(
        () => drilldownRows.reduce((s, r) => s + r.consents, 0),
        [drilldownRows]
    );

    const measurementReadyCount = useMemo(() => {
        if (selectedChannel) {
            return drilldownRows.reduce((s, r) => s + (Number(r.acceptAll) || 0), 0);
        }
        return pickMeasurementReadyCount(summary, rows);
    }, [selectedChannel, drilldownRows, summary, rows]);

    const measurementReadySharePct = useMemo(() => {
        if (selectedChannel) {
            const t = drilldownRows.reduce((s, r) => s + (Number(r.consents) || 0), 0);
            const a = drilldownRows.reduce((s, r) => s + (Number(r.acceptAll) || 0), 0);
            return t > 0 ? Math.round((a / t) * 1000) / 10 : null;
        }
        return pickMeasurementReadySharePct(summary, rows);
    }, [selectedChannel, drilldownRows, summary, rows]);

    const baselineMeasurementReadyCount = useMemo(() => {
        if (!compareUi) return null;
        if (selectedChannel) {
            return baselineRows
                .filter((r) => r.channel === selectedChannel)
                .reduce((s, r) => s + (Number(r.acceptAll) || 0), 0);
        }
        return pickMeasurementReadyCount(baselineSummary, baselineRows);
    }, [compareUi, selectedChannel, baselineRows, baselineSummary]);

    const baselineMeasurementReadySharePct = useMemo(() => {
        if (!compareUi) return null;
        if (selectedChannel) {
            const br = baselineRows.filter((r) => r.channel === selectedChannel);
            const t = br.reduce((s, r) => s + (Number(r.consents) || 0), 0);
            const a = br.reduce((s, r) => s + (Number(r.acceptAll) || 0), 0);
            return t > 0 ? Math.round((a / t) * 1000) / 10 : null;
        }
        return pickMeasurementReadySharePct(baselineSummary, baselineRows);
    }, [compareUi, selectedChannel, baselineRows, baselineSummary]);

    const drillBaselineConsents = useMemo(() => {
        if (!compareUi || !selectedChannel) return null;
        return baselineRows
            .filter((r) => r.channel === selectedChannel)
            .reduce((s, r) => s + (Number(r.consents) || 0), 0);
    }, [compareUi, baselineRows, selectedChannel]);

    const drillBaselineCampaignCount = useMemo(() => {
        if (!compareUi || !selectedChannel) return null;
        return baselineRows.filter((r) => r.channel === selectedChannel).length;
    }, [compareUi, baselineRows, selectedChannel]);

    const baselineChannelCount = useMemo(() => {
        if (!compareUi) return null;
        return buildChannelOverview(baselineRows).length;
    }, [compareUi, baselineRows]);

    const unclassifiedConsents = useMemo(
        () =>
            rows.reduce((sum, r) => {
                const c = (r.acceptAll ?? 0) + (r.essentialOnly ?? 0) + (r.granular ?? 0);
                return sum + Math.max(0, r.consents - c);
            }, 0),
        [rows]
    );

    /*
     * Analytics-visibility reframe.
     *
     * "Visible" = accept-all (measurement-ready). Everything else —
     * essential-only, granular, or unclassified — is invisible to GA4 /
     * Ads Manager / Meta pixel because the relevant analytics/marketing
     * categories weren't fully enabled. This is the single number a
     * marketer cross-referencing the dashboard with their ad platform
     * actually needs: "how much of this campaign's traffic won't reach
     * my conversion tracking?"
     */
    const visibilityScopeTotal = selectedChannel ? drillConsents : totalConsents;
    const invisibleConsents = useMemo(() => {
        const visible = Number(measurementReadyCount) || 0;
        const total = Number(visibilityScopeTotal) || 0;
        return Math.max(0, total - visible);
    }, [measurementReadyCount, visibilityScopeTotal]);

    const invisibleSharePct = useMemo(() => {
        const total = Number(visibilityScopeTotal) || 0;
        if (total <= 0) return null;
        return Math.round((invisibleConsents / total) * 1000) / 10;
    }, [invisibleConsents, visibilityScopeTotal]);

    const baselineInvisibleConsents = useMemo(() => {
        if (!compareUi) return null;
        const visible = baselineMeasurementReadyCount;
        const totalBaseline = selectedChannel ? drillBaselineConsents : totalBaselineConsents;
        if (visible == null || totalBaseline == null) return null;
        return Math.max(0, Number(totalBaseline) - Number(visible));
    }, [
        compareUi,
        baselineMeasurementReadyCount,
        selectedChannel,
        drillBaselineConsents,
        totalBaselineConsents,
    ]);

    const baselineInvisibleSharePct = useMemo(() => {
        if (!compareUi) return null;
        if (baselineMeasurementReadySharePct == null) return null;
        const s = Number(baselineMeasurementReadySharePct);
        if (!Number.isFinite(s)) return null;
        return Math.round((100 - s) * 10) / 10;
    }, [compareUi, baselineMeasurementReadySharePct]);

    /*
     * Cheap rollup of the three classifiable choice buckets across the
     * current scope (channel drill-down or full overview). The marketing
     * suggestions registry needs essential-only / granular shares to
     * fire its banner-copy rule; computing them once here avoids forcing
     * each rule to re-walk the row list.
     */
    const choiceMixTotals = useMemo(() => {
        const target = selectedChannel ? drilldownRows : rows;
        let acceptAll = 0;
        let essentialOnly = 0;
        let granular = 0;
        for (const r of target) {
            acceptAll += Number(r.acceptAll) || 0;
            essentialOnly += Number(r.essentialOnly) || 0;
            granular += Number(r.granular) || 0;
        }
        return { acceptAll, essentialOnly, granular };
    }, [rows, drilldownRows, selectedChannel]);

    const invisibleSuggestions = useMemo(
        () =>
            buildInvisibleTrafficSuggestions({
                selectedChannel,
                invisibleSharePct,
                baselineInvisibleSharePct,
                compareEnabled: compareUi,
                channelOverview,
                totalConsents: visibilityScopeTotal,
                essentialOnlyTotal: choiceMixTotals.essentialOnly,
                granularTotal: choiceMixTotals.granular,
            }),
        [
            selectedChannel,
            invisibleSharePct,
            baselineInvisibleSharePct,
            compareUi,
            channelOverview,
            visibilityScopeTotal,
            choiceMixTotals.essentialOnly,
            choiceMixTotals.granular,
        ]
    );

    /*
     * Merged geo / landing-path / utm context for the chart strip.
     * Channel view uses the subset of rows in the selected channel so the
     * "top countries" / "top paths" bars reflect that channel only. The
     * overview view pools all rows. Memoising once here keeps the charts
     * and the existing <MarketingContextSection/> tables consistent.
     */
    const mergedContext = useMemo(
        () => mergeAllContext(selectedChannel ? drilldownRows : rows),
        [selectedChannel, drilldownRows, rows]
    );

    const highlights = useMemo(
        () =>
            computeMarketingHighlights({
                selectedChannel,
                channelOverview,
                drilldownRows,
                totalConsents,
                drillConsents,
                unclassifiedConsents,
                fromDate,
                toDate,
            }),
        [
            selectedChannel,
            channelOverview,
            drilldownRows,
            totalConsents,
            drillConsents,
            unclassifiedConsents,
            fromDate,
            toDate,
        ]
    );

    const exportCsvMeta = useMemo(
        () => ({
            from: toYmd(fromDate),
            to: toYmd(toDate),
            scope: listDomainLabel,
            generatedAt: new Date().toISOString(),
            compareOn,
            compareExport: compareUi,
            compareFrom: compareOn ? toYmd(previousPeriod) : "",
            compareTo: compareOn ? toYmd(previousPeriod2) : "",
        }),
        [fromDate, toDate, listDomainLabel, compareOn, compareUi, previousPeriod, previousPeriod2]
    );

    const exportFilenameBase = useMemo(() => {
        const safe = String(listDomainLabel || "report")
            .replace(/[^\w\-]+/g, "_")
            .slice(0, 60);
        return `marketing_${toYmd(fromDate)}_${toYmd(toDate)}_${safe}`;
    }, [listDomainLabel, fromDate, toDate]);

    useEffect(() => {
        if (!selectedChannel) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") setSelectedChannel(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [selectedChannel]);

    useEffect(() => {
        setChannelTableSort({ column: null, step: 0 });
        setCampaignTableSort({ column: null, step: 0 });
    }, [selectedChannel]);

    const kpiCards = useMemo(() => {
        const cards = [
            {
                key: "channels",
                label: selectedChannel ? "Campaigns in channel" : "Channels",
                value: selectedChannel
                    ? drilldownRows.length.toLocaleString("de-DE")
                    : channelOverview.length.toLocaleString("de-DE"),
                hint: null,
                compare:
                    compareUi && selectedChannel && drillBaselineCampaignCount != null ? (
                        <MarketingCompareInline
                            kind="volume"
                            current={drilldownRows.length}
                            previous={drillBaselineCampaignCount}
                        />
                    ) : compareUi && !selectedChannel && baselineChannelCount != null ? (
                        <MarketingCompareInline
                            kind="volume"
                            current={channelOverview.length}
                            previous={baselineChannelCount}
                        />
                    ) : null,
            },
            {
                key: "consents",
                label: selectedChannel ? "Consents (this channel)" : "Consents in view",
                value: (selectedChannel ? drillConsents : totalConsents).toLocaleString("de-DE"),
                hint: selectedChannel
                    ? null
                    : "Total includes all attributed rows for this period (not only the table page).",
                compare:
                    compareUi && selectedChannel && drillBaselineConsents != null ? (
                        <MarketingCompareInline kind="volume" current={drillConsents} previous={drillBaselineConsents} />
                    ) : compareUi && !selectedChannel && totalBaselineConsents != null ? (
                        <MarketingCompareInline kind="volume" current={totalConsents} previous={totalBaselineConsents} />
                    ) : null,
            },
            {
                key: "measurement-ready",
                label: selectedChannel ? "Full-stack consents (channel)" : "Full-stack consent events",
                value: measurementReadyCount.toLocaleString("de-DE"),
                hint: "Accept-all choices: optional analytics & marketing categories fully on. Useful for budget discussions — not revenue or ROAS.",
                compare:
                    compareUi && baselineMeasurementReadyCount != null ? (
                        <MarketingCompareInline
                            kind="volume"
                            current={measurementReadyCount}
                            previous={baselineMeasurementReadyCount}
                        />
                    ) : null,
            },
            {
                key: "measurement-share",
                label: selectedChannel ? "Full-stack share (channel)" : "Full-stack share of tagged traffic",
                value:
                    measurementReadySharePct != null && Number.isFinite(measurementReadySharePct)
                        ? `${measurementReadySharePct.toLocaleString("de-DE", { maximumFractionDigits: 1 })}%`
                        : "—",
                hint: "Share of attributed consent events in this view with accept-all. Compare periods to see if spend reaches consenting audiences.",
                compare:
                    compareUi &&
                    baselineMeasurementReadySharePct != null &&
                    measurementReadySharePct != null &&
                    Number.isFinite(measurementReadySharePct) &&
                    Number.isFinite(baselineMeasurementReadySharePct) ? (
                        <MarketingCompareInline
                            kind="rate"
                            current={measurementReadySharePct}
                            previous={baselineMeasurementReadySharePct}
                        />
                    ) : null,
            },
            /*
             * The reconciliation-first KPI: this is the number a marketer
             * looking at GA4 / Ads Manager actually cares about. We show
             * it with a warning variant so it reads as something to act
             * on, not just a neutral fact. Growth is a regression, so the
             * compare delta uses `lowerIsBetter` to flip up/down colors.
             */
            {
                key: "invisible-traffic",
                label: selectedChannel
                    ? "Analytics-invisible (channel)"
                    : "Analytics-invisible traffic",
                value: invisibleConsents.toLocaleString("de-DE"),
                hint:
                    invisibleSharePct != null
                        ? `${invisibleSharePct.toLocaleString("de-DE", { maximumFractionDigits: 1 })}% of attributed consents chose essential-only or granular — these visits won't reach GA4, Ads Manager, or the Meta pixel.`
                        : "Attributed consents that chose essential-only or granular — they won't reach GA4, Ads Manager, or the Meta pixel.",
                variant: "warn",
                compare:
                    compareUi && baselineInvisibleConsents != null ? (
                        <MarketingCompareInline
                            kind="volume"
                            current={invisibleConsents}
                            previous={baselineInvisibleConsents}
                            lowerIsBetter
                        />
                    ) : null,
            },
        ];
        if (summary && typeof summary === "object") {
            if (summary.sessionsWithMarketingParams != null) {
                cards.push({
                    key: "sessions-marketing",
                    label: "Sessions w/ marketing params",
                    value: String(summary.sessionsWithMarketingParams),
                    hint: null,
                    compare: null,
                });
            }
            if (summary.distinctCampaigns != null) {
                cards.push({
                    key: "distinct-campaigns",
                    label: "Distinct campaigns",
                    value: String(summary.distinctCampaigns),
                    hint: null,
                    compare: null,
                });
            }
        }
        // GA4 coverage KPIs — only show on overview (not per-channel drilldown)
        if (!selectedChannel && ga4Summary && ga4Summary.sessions > 0) {
            const ga4Sess = ga4Summary.sessions;
            const capturePct = Math.round((totalConsents / ga4Sess) * 1000) / 10;
            const darkZone = Math.max(0, ga4Sess - totalConsents);
            cards.push({
                key: "ga4-coverage",
                label: "GA4 consent coverage",
                value: Number.isFinite(capturePct) ? `${capturePct}%` : "—",
                hint: `${totalConsents.toLocaleString("de-DE")} consent events from ${ga4Sess.toLocaleString("de-DE")} GA4 sessions. Sessions that did not generate a consent event are the dark zone.`,
                variant: capturePct < 15 ? "warn" : null,
                compare: null,
            });
            cards.push({
                key: "ga4-dark-zone",
                label: "Session dark zone",
                value: darkZone.toLocaleString("de-DE"),
                hint: "GA4 sessions that reached the site but generated no consent event. Includes returning visitors with prior consent, bounces before banner load, and bot traffic.",
                variant: "warn",
                compare: null,
            });
        }
        return cards;
    }, [
        totalConsents,
        drillConsents,
        summary,
        selectedChannel,
        drilldownRows.length,
        channelOverview.length,
        compareUi,
        drillBaselineCampaignCount,
        drillBaselineConsents,
        baselineChannelCount,
        totalBaselineConsents,
        measurementReadyCount,
        measurementReadySharePct,
        baselineMeasurementReadyCount,
        baselineMeasurementReadySharePct,
        invisibleConsents,
        invisibleSharePct,
        baselineInvisibleConsents,
        ga4Summary,
    ]);

    return (
        <>
            <div style={{ flex: "1" }}>
                <StickyPageTitle
                    loadingUpdated={loading}
                    finalLoaded={loading}
                    title="Channel Analytics"
                    numberofDays={setLastDays}
                    getLastDays={getLastDays}
                    setActiveData={setActiveData}
                    fromDate={fromDate}
                    toDate={toDate}
                    setFromDate={setFromDate}
                    setToDate={setToDate}
                    previousPeriod={previousPeriod}
                    previousPeriod2={previousPeriod2}
                    compareRange={compareRange}
                    setCompareRange={setCompareRange}
                    setCompareWindowStart={setPreviousPeriod}
                    setCompareWindowEnd={setPreviousPeriod2}
                />
                <div className="dashboard-content marketing-report-page">

                    {/* ── Compact page header ─────────────────────────────── */}
                    <div className="ca-page-header">
                        <div className="ca-page-header__left">
                            {/* "Channel Analytics" itself is already shown by StickyPageTitle above —
                                this row only needs a heading once drilled into a specific channel. */}
                            {selectedChannel && (
                                <h1 className="ca-page-header__title">
                                    <button className="ca-back-btn" onClick={() => setSelectedChannel(null)}>
                                        ← Channels
                                    </button>
                                    <span className="ca-page-header__channel">{selectedChannel}</span>
                                </h1>
                            )}
                            {compareOn && (
                                <span className="ca-compare-pill">
                                    {formatPeriodRange(fromDate, toDate)} vs {formatPeriodRange(previousPeriod, previousPeriod2)}
                                </span>
                            )}
                        </div>
                        <a href={reportsPath(id, listDomainLabel, "/reconcile")}
                           className="ca-reconcile-link">
                            Ad Reconciliation →
                        </a>
                    </div>

                    {compareBaselineNote ? (
                        <p className="marketing-report-compare-warning" role="status">
                            {compareBaselineNote}
                        </p>
                    ) : null}

                    {error ? (
                        <div className="marketing-report-error" role="alert">{error}</div>
                    ) : null}

                    {/* ── Consent KPI strip ───────────────────────────────── */}
                    {!error && rows.length > 0 && (
                        <div className="ca-kpi-strip">
                            {kpiCards.slice(0, 5).map((c) => (
                                <div key={c.key || c.label}
                                     className={["ca-kpi", c.variant ? `ca-kpi--${c.variant}` : ""].filter(Boolean).join(" ")}
                                     title={c.hint || ""}>
                                    <span className="ca-kpi__value">{c.value}</span>
                                    <span className="ca-kpi__label">{c.label}</span>
                                    {c.compare ? <div className="ca-kpi__compare">{c.compare}</div> : null}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ── Ad Performance ──────────────────────────────────── */}
                    {!selectedChannel && !adPerfLoading && adPerfData?.platforms?.length > 0 && (
                        <div className="ca-ad-perf">
                            <h3 className="ca-ad-perf__title">Ad Performance</h3>
                            <div className="ca-ad-perf__cards">
                                {adPerfData.platforms.map(p => {
                                    const matched = consentsForAdPlatform(channelOverview, p.platform);
                                    const cpc = p.clicks > 0 ? p.amount / p.clicks : null;
                                    const cpconsent = matched > 0 ? p.amount / matched : null;
                                    return (
                                        <div key={p.platform} className="ca-ad-perf-card">
                                            <span className="ca-ad-perf-card__platform">
                                                {AD_PLATFORM_LABELS[p.platform] || p.platform}
                                            </span>
                                            <div className="ca-ad-perf-card__metrics">
                                                <span className="ca-ad-perf-card__metric">
                                                    <span className="ca-ad-perf-card__val">{formatAdMoney(p.amount, p.currency)}</span>
                                                    <span className="ca-ad-perf-card__lbl">spend</span>
                                                </span>
                                                <span className="ca-ad-perf-card__metric">
                                                    <span className="ca-ad-perf-card__val">{p.clicks.toLocaleString("de-DE")}</span>
                                                    <span className="ca-ad-perf-card__lbl">clicks</span>
                                                </span>
                                                <span className="ca-ad-perf-card__metric">
                                                    <span className="ca-ad-perf-card__val">{p.impressions.toLocaleString("de-DE")}</span>
                                                    <span className="ca-ad-perf-card__lbl">impressions</span>
                                                </span>
                                                {cpc != null && (
                                                    <span className="ca-ad-perf-card__metric">
                                                        <span className="ca-ad-perf-card__val">{formatAdMoney(cpc, p.currency)}</span>
                                                        <span className="ca-ad-perf-card__lbl">CPC</span>
                                                    </span>
                                                )}
                                                {cpconsent != null && (
                                                    <span className="ca-ad-perf-card__metric ca-ad-perf-card__metric--accent">
                                                        <span className="ca-ad-perf-card__val">{formatAdMoney(cpconsent, p.currency)}</span>
                                                        <span className="ca-ad-perf-card__lbl">cost / consent</span>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* ── Highlights ──────────────────────────────────────── */}
                    {!error && !(loading && rows.length === 0) ? (
                        <MarketingHighlightsSection highlights={highlights} />
                    ) : null}

                    {/* ── Suggestions ─────────────────────────────────────── */}
                    {!error && rows.length > 0 && invisibleSuggestions.length > 0 ? (
                        <MarketingSuggestionsStrip
                            suggestions={invisibleSuggestions}
                            domainKey={listDomainLabel}
                        />
                    ) : null}

                    {/* ── Main table ──────────────────────────────────────── */}
                    <section className="marketing-report-section ca-table-section" aria-labelledby="detail-table-heading-top">
                        <div className="ca-table-header">
                            <h2 id="detail-table-heading-top" className="marketing-report-section__title" style={{margin:0}}>
                                {selectedChannel ? `Campaigns · ${selectedChannel}` : "Channels & campaigns"}
                            </h2>
                            <div className="ca-table-toolbar">
                                {rows.length > 0 && (
                                    <>
                                        <button type="button" className="marketing-report-export"
                                            onClick={() => {
                                                const data = selectedChannel ? sortedDrilldownRows : rows;
                                                const slug = selectedChannel ? selectedChannel.replace(/[^\w\-]+/g, "_").slice(0, 48) : "";
                                                triggerCsvDownload(`${exportFilenameBase}_campaigns${slug ? `_${slug}` : ""}.csv`, buildMarketingCsvCampaignRows(data, exportCsvMeta));
                                            }}>
                                            {selectedChannel ? "Export campaigns CSV" : "Export campaigns CSV"}
                                        </button>
                                        <button type="button" className="marketing-report-export marketing-report-export--secondary"
                                            onClick={() => triggerCsvDownload(`${exportFilenameBase}_channels.csv`, buildMarketingCsvChannelRows(sortedChannelOverview, exportCsvMeta))}>
                                            Export channels CSV
                                        </button>
                                    </>
                                )}
                                <span className="marketing-report-toolbar__meta">
                                    {loading ? "Loading…"
                                        : selectedChannel
                                          ? `${drilldownRows.length} campaign${drilldownRows.length === 1 ? "" : "s"} · ${drilldownRows.reduce((s, r) => s + r.consents, 0).toLocaleString("de-DE")} consents`
                                          : `${channelOverview.length} channel${channelOverview.length === 1 ? "" : "s"} · ${totalConsents.toLocaleString("de-DE")} consents`}
                                </span>
                            </div>
                        </div>
                        <div className="marketing-report-table-wrap">
                        {rows.length === 0 && !loading ? (
                            <div className="marketing-report-empty">
                                No marketing rows for this scope and period. When your API returns data, it will appear
                                here.
                            </div>
                        ) : selectedChannel ? (
                            <table className="marketing-report-table marketing-report-table--with-choices">
                                <thead>
                                    <tr>
                                        <MarketingTableSortTh
                                            label="Campaign name"
                                            columnKey="utmCampaign"
                                            kind={CAMPAIGN_SORT_KEYS.utmCampaign}
                                            sortState={campaignTableSort}
                                            onCycle={cycleCampaignTableSort}
                                            className="marketing-report-table__col-campaign"
                                        />
                                        <MarketingTableSortTh
                                            label="Consents"
                                            columnKey="consents"
                                            kind={CAMPAIGN_SORT_KEYS.consents}
                                            sortState={campaignTableSort}
                                            onCycle={cycleCampaignTableSort}
                                            className="marketing-report-table__col-num"
                                        />
                                        <MarketingTableSortTh
                                            label="Acceptance %"
                                            columnKey="acceptPct"
                                            kind={CAMPAIGN_SORT_KEYS.acceptPct}
                                            sortState={campaignTableSort}
                                            onCycle={cycleCampaignTableSort}
                                            className="marketing-report-table__col-num"
                                        />
                                        <MarketingTableSortTh
                                            label="Accept all"
                                            columnKey="acceptAll"
                                            kind={CAMPAIGN_SORT_KEYS.acceptAll}
                                            sortState={campaignTableSort}
                                            onCycle={cycleCampaignTableSort}
                                            className="marketing-report-table__col-choice"
                                        />
                                        <MarketingTableSortTh
                                            label="Essential only"
                                            columnKey="essentialOnly"
                                            kind={CAMPAIGN_SORT_KEYS.essentialOnly}
                                            sortState={campaignTableSort}
                                            onCycle={cycleCampaignTableSort}
                                            className="marketing-report-table__col-choice"
                                        />
                                        <MarketingTableSortTh
                                            label="Granular"
                                            columnKey="granular"
                                            kind={CAMPAIGN_SORT_KEYS.granular}
                                            sortState={campaignTableSort}
                                            onCycle={cycleCampaignTableSort}
                                            className="marketing-report-table__col-choice"
                                        />
                                    </tr>
                                </thead>
                                <tbody>
                                    {drilldownRows.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="marketing-report-table__empty-row">
                                                No campaigns for this channel.
                                            </td>
                                        </tr>
                                    ) : (
                                        sortedDrilldownRows.map((r, i) => {
                                            const blindSpot = isAnalyticsBlindSpot(r);
                                            return (
                                            <tr
                                                key={`${r.channel}-${r.utmCampaign}-${i}`}
                                                className={
                                                    blindSpot
                                                        ? "marketing-report-table__row--blind-spot"
                                                        : undefined
                                                }
                                            >
                                                <td className="marketing-report-table__col-campaign">
                                                    {(() => {
                                                        const platform = channelToPlatform(selectedChannel);
                                                        const resolved = platform && mrIsNumericId(r.utmCampaign)
                                                            ? resolvedMrCampaigns[`${platform}:${r.utmCampaign}`]
                                                            : null;
                                                        return resolved
                                                            ? <span title={`ID: ${r.utmCampaign}`}>{resolved.name}</span>
                                                            : r.utmCampaign;
                                                    })()}
                                                    {blindSpot ? (
                                                        <span
                                                            className="marketing-report-blind-spot-badge"
                                                            title={`Under ${BLIND_SPOT_MAX_VISIBILITY_PCT}% of this campaign's consents will reach your analytics tools.`}
                                                        >
                                                            analytics blind spot
                                                        </span>
                                                    ) : null}
                                                </td>
                                                <td className="marketing-report-table__col-num">
                                                    <MarketingMetricStack
                                                        compareUi={compareUi}
                                                        primary={r.consents.toLocaleString("de-DE")}
                                                        current={r.consents}
                                                        previous={r.prevConsents}
                                                        kind="volume"
                                                    />
                                                </td>
                                                <td className="marketing-report-table__col-num">
                                                    <MarketingMetricStack
                                                        compareUi={compareUi}
                                                        primary={
                                                            r.acceptPct != null && Number.isFinite(r.acceptPct)
                                                                ? `${r.acceptPct.toLocaleString("de-DE", { maximumFractionDigits: 1 })}%`
                                                                : "—"
                                                        }
                                                        current={r.acceptPct}
                                                        previous={r.prevAcceptPct}
                                                        kind="rate"
                                                    />
                                                </td>
                                                <td className="marketing-report-table__col-choice">
                                                    {formatChoiceCountPct(r.acceptAll, r.consents)}
                                                </td>
                                                <td className="marketing-report-table__col-choice">
                                                    {formatChoiceCountPct(r.essentialOnly, r.consents)}
                                                </td>
                                                <td className="marketing-report-table__col-choice">
                                                    {formatChoiceCountPct(r.granular, r.consents)}
                                                </td>
                                            </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        ) : (
                            <>
                            <table className="marketing-report-table marketing-report-table--with-choices">
                                <thead>
                                    <tr>
                                        <MarketingTableSortTh
                                            label="Channel"
                                            columnKey="channel"
                                            kind={CHANNEL_SORT_KEYS.channel}
                                            sortState={channelTableSort}
                                            onCycle={cycleChannelTableSort}
                                            className="marketing-report-table__col-channel"
                                        />
                                        <MarketingTableSortTh
                                            label="Campaigns"
                                            columnKey="campaignCount"
                                            kind={CHANNEL_SORT_KEYS.campaignCount}
                                            sortState={channelTableSort}
                                            onCycle={cycleChannelTableSort}
                                            className="marketing-report-table__col-num"
                                        />
                                        <MarketingTableSortTh
                                            label="Consents"
                                            columnKey="consents"
                                            kind={CHANNEL_SORT_KEYS.consents}
                                            sortState={channelTableSort}
                                            onCycle={cycleChannelTableSort}
                                            className="marketing-report-table__col-num"
                                        />
                                        <MarketingTableSortTh
                                            label="Acceptance %"
                                            columnKey="acceptPct"
                                            kind={CHANNEL_SORT_KEYS.acceptPct}
                                            sortState={channelTableSort}
                                            onCycle={cycleChannelTableSort}
                                            className="marketing-report-table__col-num"
                                        />
                                        <MarketingTableSortTh
                                            label="Accept all"
                                            columnKey="acceptAll"
                                            kind={CHANNEL_SORT_KEYS.acceptAll}
                                            sortState={channelTableSort}
                                            onCycle={cycleChannelTableSort}
                                            className="marketing-report-table__col-choice"
                                        />
                                        <MarketingTableSortTh
                                            label="Essential only"
                                            columnKey="essentialOnly"
                                            kind={CHANNEL_SORT_KEYS.essentialOnly}
                                            sortState={channelTableSort}
                                            onCycle={cycleChannelTableSort}
                                            className="marketing-report-table__col-choice"
                                        />
                                        <MarketingTableSortTh
                                            label="Granular"
                                            columnKey="granular"
                                            kind={CHANNEL_SORT_KEYS.granular}
                                            sortState={channelTableSort}
                                            onCycle={cycleChannelTableSort}
                                            className="marketing-report-table__col-choice"
                                        />
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedChannelOverview.map((r) => {
                                        const blindSpot = isAnalyticsBlindSpot(r);
                                        return (
                                        <tr
                                            key={r.channel}
                                            className={[
                                                "marketing-report-table__row--clickable",
                                                blindSpot ? "marketing-report-table__row--blind-spot" : "",
                                            ]
                                                .filter(Boolean)
                                                .join(" ")}
                                            tabIndex={0}
                                            role="button"
                                            aria-label={
                                                blindSpot
                                                    ? `Open campaign breakdown for ${r.channel} — analytics blind spot`
                                                    : `Open campaign breakdown for ${r.channel}`
                                            }
                                            onClick={() => setSelectedChannel(r.channel)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    setSelectedChannel(r.channel);
                                                }
                                            }}
                                        >
                                            <td className="marketing-report-table__col-channel">
                                                <span className="marketing-report-channel-cell">
                                                    <span className="marketing-report-channel-cell__label">
                                                        {r.channel}
                                                    </span>
                                                    {blindSpot ? (
                                                        <span
                                                            className="marketing-report-blind-spot-badge"
                                                            title={`Under ${BLIND_SPOT_MAX_VISIBILITY_PCT}% of ${r.channel}'s consents will reach your analytics tools.`}
                                                        >
                                                            analytics blind spot
                                                        </span>
                                                    ) : null}
                                                    <span className="marketing-report-channel-cell__chevron" aria-hidden>
                                                        →
                                                    </span>
                                                </span>
                                            </td>
                                            <td className="marketing-report-table__col-num">
                                                <MarketingMetricStack
                                                    compareUi={compareUi}
                                                    primary={r.campaignCount.toLocaleString("de-DE")}
                                                    current={r.campaignCount}
                                                    previous={r.prevCampaignCount}
                                                    kind="volume"
                                                />
                                            </td>
                                            <td className="marketing-report-table__col-num">
                                                <MarketingMetricStack
                                                    compareUi={compareUi}
                                                    primary={r.consents.toLocaleString("de-DE")}
                                                    current={r.consents}
                                                    previous={r.prevConsents}
                                                    kind="volume"
                                                />
                                            </td>
                                            <td className="marketing-report-table__col-num">
                                                <MarketingMetricStack
                                                    compareUi={compareUi}
                                                    primary={
                                                        r.acceptPct != null && Number.isFinite(r.acceptPct)
                                                            ? `${r.acceptPct.toLocaleString("de-DE", { maximumFractionDigits: 1 })}%`
                                                            : "—"
                                                    }
                                                    current={r.acceptPct}
                                                    previous={r.prevAcceptPct}
                                                    kind="rate"
                                                />
                                            </td>
                                            <td className="marketing-report-table__col-choice">
                                                {formatChoiceCountPct(r.acceptAll, r.consents)}
                                            </td>
                                            <td className="marketing-report-table__col-choice">
                                                {formatChoiceCountPct(r.essentialOnly, r.consents)}
                                            </td>
                                            <td className="marketing-report-table__col-choice">
                                                {formatChoiceCountPct(r.granular, r.consents)}
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            <MarketingTablePagination
                                page={channelTablePage}
                                pageCount={channelTablePageCount}
                                totalRows={sortedChannelOverview.length}
                                pageSize={CHANNEL_TABLE_PAGE_SIZE}
                                onPageChange={setChannelTablePage}
                                label="channels"
                            />
                            </>
                        )}
                    </div>
                    </section>
                    {rows.length > 0 && unclassifiedConsents > 0 ? (
                        <p className="marketing-report-footnote" role="note">
                            {unclassifiedConsents.toLocaleString("de-DE")} consent
                            {unclassifiedConsents === 1 ? "" : "s"} in this period had no classifiable choice pattern
                            (missing or legacy payload). They are included in <strong>Consents</strong> but not in the
                            three choice columns.
                        </p>
                    ) : null}

                    {/* ── Channel charts (overview or drill-in) ───────────── */}
                    {!error && rows.length > 0 ? (
                        selectedChannel ? (
                            <>
                                <MarketingChannelCharts
                                    channelName={selectedChannel}
                                    drilldownRows={drilldownRows}
                                    drillConsents={drillConsents}
                                    mergedContext={mergedContext}
                                />
                                <MarketingTimeseriesChart
                                    channelName={selectedChannel}
                                    deriveChannel={deriveMarketingChannel}
                                    timeseriesRows={timeseriesRows}
                                    baselineTimeseriesRows={baselineTimeseriesRows}
                                    fromDate={fromDate}
                                    toDate={toDate}
                                    compareEnabled={compareUi}
                                    loading={timeseriesLoading}
                                    errorMessage={timeseriesError}
                                />
                            </>
                        ) : (
                            <MarketingOverviewCharts
                                channelOverview={channelOverview}
                                rows={rows}
                            />
                        )
                    ) : null}

                    {/* ── What's driving differences (collapsed by default) ── */}
                    {rows.length > 0 ? (
                        <details className="ca-context-details">
                            <summary className="ca-context-summary">
                                What's driving differences
                                <span className="ca-context-summary__hint">geography · landing paths · UTM variants</span>
                            </summary>
                            <MarketingContextSection
                                heading={selectedChannel ? `${selectedChannel} · detail` : "All channels · detail"}
                                rows={selectedChannel ? drilldownRows : rows}
                            />
                        </details>
                    ) : null}

                </div>
            </div>
        </>
    );
}
